import React, { useState, useRef, useCallback, useEffect, useMemo } from 'react'
import ExcelJS from 'exceljs'
import JSZip from 'jszip'
import {
  Box,
  Paper,
  Typography,
  Button,
  Alert,
  Chip,
  Grid,
  Stepper,
  Step,
  StepLabel,
  Card,
  CardContent,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Collapse,
  IconButton,
  LinearProgress,
  Tooltip,
  Snackbar,
  Stack,
  Badge,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  CircularProgress,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  TextField,
  Autocomplete,
  Popper,
  ClickAwayListener,
  Radio,
  RadioGroup,
  FormControlLabel,
} from '@mui/material'
import SecurityIcon from '@mui/icons-material/Security'
import CloudIcon from '@mui/icons-material/Cloud'
import StorageIcon from '@mui/icons-material/Storage'
import CloudUploadIcon from '@mui/icons-material/CloudUpload'
import DownloadIcon from '@mui/icons-material/Download'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import ErrorIcon from '@mui/icons-material/Error'
import WarningIcon from '@mui/icons-material/Warning'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import DeleteIcon from '@mui/icons-material/Delete'
import InfoIcon from '@mui/icons-material/Info'
import ArticleIcon from '@mui/icons-material/Article'
import SendIcon from '@mui/icons-material/Send'
import RefreshIcon from '@mui/icons-material/Refresh'
import LockIcon from '@mui/icons-material/Lock'
import SearchIcon from '@mui/icons-material/Search'
import AddIcon from '@mui/icons-material/Add'
import EditIcon from '@mui/icons-material/Edit'
import { certificateApi, awsCertAccountApi, inventoryCertificateApi, itsmProductApi } from '../utils/api'

// ─── CSV Column Definitions ──────────────────────────────────────────────────

const AWS_COLUMNS = [
  { key: 'common_name',              label: 'common_name',              required: true,  description: 'Certificate common name (e.g. app.broadridge.com)' },
  { key: 'account_name',             label: 'account_name',             required: true,  description: 'AWS account name from onboarded accounts' },
  { key: 'itsm_group',               label: 'itsm_group',               required: true,  description: 'ITSM assignment group of the team managing the cert (e.g. ICS-DT-CERT-MGMT)' },
  { key: 'venafi_policy_base',       label: 'venafi_policy_base',       required: true,  description: 'prod-digicert | prod-private | nonprod-digicert | nonprod-private' },
  { key: 'mutual_auth',              label: 'mutual_auth',              required: true,  description: 'yes | no' },
  { key: 'layer7',                   label: 'layer7',                   required: false, description: 'yes | no  (required only if mutual_auth=yes)' },
  { key: 'layer7_endpoint',          label: 'layer7_endpoint',          required: false, description: 'Required only if layer7=yes' },
  { key: 'dependency_present',       label: 'notes_present',            required: true,  description: 'true | false — are there any notes to add?' },
  { key: 'dependencies',             label: 'notes',                    required: false, description: 'Pipe-separated notes (required if notes_present=true)' },
  { key: 'aws_cert_in_onprem',       label: 'aws_cert_in_onprem',       required: true,  description: 'yes | no — is cert also used on-premises?' },
  { key: 'acm_parameter',            label: 'acm_parameter',            required: true,  description: 'yes | no — store in ACM?' },
  { key: 'existing_acm_arn1',        label: 'existing_acm_arn1',        required: false, description: 'Existing ACM ARN #1 (only if acm_parameter=yes and cert already in ACM)' },
  { key: 'existing_acm_arn2',        label: 'existing_acm_arn2',        required: false, description: 'Existing ACM ARN #2 (only if acm_parameter=yes and a second region ARN exists)' },
  { key: 'ssm_parameter',            label: 'ssm_parameter',            required: true,  description: 'yes | no — store in SSM Parameter Store?' },
  { key: 'ssm_format',               label: 'ssm_format',               required: false, description: 'PEM | PFX  (required if ssm_parameter=yes). Paths and content/param types are auto-computed.' },
  { key: 'sm_parameter',             label: 'sm_parameter',             required: true,  description: 'yes | no — store in Secrets Manager?' },
  { key: 'sm_format',                label: 'sm_format',                required: false, description: 'PEM | PFX | JKS  (required if sm_parameter=yes). Path is auto-computed.' },
  { key: 'environment',              label: 'environment',              required: true,  description: 'SDLC environment: dev | qa | uat | prd' },
  { key: 'product_code',             label: 'product_code',             required: true,  description: 'ITSM product code (auto-filled from account)' },
  { key: 'account_number',           label: 'account_number',           required: true,  description: 'AWS account number (12-digit, auto-filled from account)' },
  { key: 'application_id',           label: 'application_id',           required: true,  description: 'Application UUID (brcp:application:id, auto-filled from account)' },
]

const ONPREM_COLUMNS = [
  { key: 'common_name',         label: 'common_name',         required: true,  description: 'Certificate common name (e.g. app.broadridge.com)' },
  { key: 'product_name',        label: 'product_name',        required: true,  description: 'ITSM product name for display' },
  { key: 'itsm_group',          label: 'itsm_group',          required: true,  description: 'ITSM assignment group of the team managing the cert (e.g. ICS-DT-CERT-MGMT)' },
  { key: 'environment',         label: 'environment',         required: false, description: 'SDLC environment: dev | qa | uat | prd (auto-detected from Venafi if not provided)' },
  { key: 'product_code',        label: 'product_code',        required: true,  description: 'ITSM product code (auto-filled from product name)' },
]

// ─── Policy base detection from Venafi DN ────────────────────────────────────

const detectPolicyBase = (policyDn) => {
  if (!policyDn) return ''
  const dn = policyDn.toLowerCase()
  if (dn.includes('non-production\\brcc digicert standard ca'))      return 'nonprod-digicert'
  if (dn.includes('non-production\\brcc entrust private gateway ca')) return 'nonprod-private'
  if (dn.includes('\\production\\brcc digicert standard ca'))         return 'prod-digicert'
  if (dn.includes('\\production\\brcc entrust private gateway ca'))   return 'prod-private'
  return ''
}

// ─── SDLC environment detection from common name ─────────────────────────────
// Matches env tokens at word boundaries (hyphens, dots, underscores, or string edges).

const detectEnvFromCn = (cn) => {
  if (!cn) return ''
  const s = cn.toLowerCase()
  const has = (token) => new RegExp(`(^|[.\\-_])${token}([.\\-_]|$)`).test(s)
  if (has('prd') || has('prod')) return 'prd'
  if (has('uat'))  return 'uat'
  if (has('qa'))   return 'qa'
  if (has('dev'))  return 'dev'
  return ''
}

// ─── Validation ──────────────────────────────────────────────────────────────

const VALID_ENVIRONMENTS  = ['dev', 'qa', 'uat', 'prd']
const VALID_CERT_USAGES   = ['server-internal', 'server-public', 'client-internal', 'client-broadridge', 'client-external', 'other']
const VALID_POLICY_BASES  = ['prod-digicert', 'prod-private', 'nonprod-digicert', 'nonprod-private']
const VALID_YES_NO        = ['yes', 'no']
const VALID_TRUE_FALSE    = ['true', 'false']
const VALID_SSM_FORMATS   = ['PEM', 'PFX']
const VALID_SM_FORMATS    = ['PEM', 'PFX', 'JKS']
const VALID_CONTENT_TYPES = ['String', 'SecureString']
const VALID_PARAM_TYPES   = ['Standard', 'Advanced']

// ─── Normalizer ─────────────────────────────────────────────────────────────
// Matches any input case to the canonical valid value; leaves free-text fields as-is.

const matchCanonical = (value, validValues) => {
  if (!value) return value
  const trimmed = String(value).trim()
  const found = validValues.find(v => v.toLowerCase() === trimmed.toLowerCase())
  return found !== undefined ? found : trimmed
}

const normalizeRow = (row) => {
  const r = { ...row }
  // Fields that must be lowercase
  const lcFields = [
    'mutual_auth', 'layer7', 'dependency_present', 'environment',
    'certificate_usage', 'venafi_policy_base',
    'aws_cert_in_onprem', 'acm_parameter', 'ssm_parameter', 'sm_parameter',
  ]
  lcFields.forEach(f => { if (r[f]) r[f] = String(r[f]).trim().toLowerCase() })

  // Fields with mixed/ProperCase valid values — match to canonical
  const canonicalFields = [
    { field: 'ssm_format',               valid: VALID_SSM_FORMATS   },
    { field: 'sm_format',                valid: VALID_SM_FORMATS    },
    { field: 'ssm_cert_content_type',    valid: VALID_CONTENT_TYPES },
    { field: 'ssm_cert_param_type',      valid: VALID_PARAM_TYPES   },
    { field: 'ssm_key_content_type',     valid: VALID_CONTENT_TYPES },
    { field: 'ssm_key_param_type',       valid: VALID_PARAM_TYPES   },
    { field: 'ssm_chain_content_type',   valid: VALID_CONTENT_TYPES },
    { field: 'ssm_chain_param_type',     valid: VALID_PARAM_TYPES   },
    { field: 'ssm_password_content_type',valid: VALID_CONTENT_TYPES },
    { field: 'ssm_password_param_type',  valid: VALID_PARAM_TYPES   },
  ]
  canonicalFields.forEach(({ field, valid }) => {
    if (r[field]) r[field] = matchCanonical(r[field], valid)
  })

  return r
}

const validateRow = (row, deploymentType) => {
  const errors = []
  const warnings = []
  const cols = deploymentType === 'aws' ? AWS_COLUMNS : ONPREM_COLUMNS

  // Required field presence
  cols.filter(c => c.required).forEach(col => {
    if (!row[col.key] || String(row[col.key]).trim() === '') {
      errors.push(`"${col.key}" is required`)
    }
  })

  // For on-prem, only common_name/product_code/product_name/itsm_group are required.
  // certificate_usage is always auto-fetched from Venafi.
  if (deploymentType === 'onprem') {
    return { errors, warnings, isValid: errors.length === 0 }
  }

  // AWS validations (full)
  if (row.environment && !VALID_ENVIRONMENTS.includes(row.environment))
    errors.push(`"environment" must be one of: ${VALID_ENVIRONMENTS.join(', ')}`)

  if (row.certificate_usage && !VALID_CERT_USAGES.includes(row.certificate_usage))
    errors.push(`"certificate_usage" must be one of: ${VALID_CERT_USAGES.join(', ')}`)

  if (row.venafi_policy_base && !VALID_POLICY_BASES.includes(row.venafi_policy_base))
    errors.push(`"venafi_policy_base" must be one of: ${VALID_POLICY_BASES.join(', ')}`)

  if (row.mutual_auth && !VALID_YES_NO.includes(row.mutual_auth))
    errors.push('"mutual_auth" must be yes or no')

  if (row.dependency_present && !VALID_TRUE_FALSE.includes(row.dependency_present))
    errors.push('"dependency_present" must be true or false')

  // Conditional validations
  if (row.mutual_auth === 'yes' && !row.layer7)
    errors.push('"layer7" is required when mutual_auth=yes')

  if (row.layer7 === 'yes' && !row.layer7_endpoint)
    errors.push('"layer7_endpoint" is required when layer7=yes')

  if (row.dependency_present === 'true' && !row.dependencies)
    errors.push('"dependencies" required when dependency_present=true')

  if (row.ssm_parameter && !VALID_YES_NO.includes(row.ssm_parameter))
    errors.push('"ssm_parameter" must be yes or no')

  if (row.sm_parameter && !VALID_YES_NO.includes(row.sm_parameter))
    errors.push('"sm_parameter" must be yes or no')

  if (row.acm_parameter && !VALID_YES_NO.includes(row.acm_parameter))
    errors.push('"acm_parameter" must be yes or no')

  if (row.acm_parameter === 'yes') {
    const arn1 = row.existing_acm_arn1?.trim()
    const arn2 = row.existing_acm_arn2?.trim()
    if (!arn1 && !arn2)
      errors.push('"existing_acm_arn1" or "existing_acm_arn2": at least one ACM ARN is required when acm_parameter=yes')
  }

  if (row.aws_cert_in_onprem && !VALID_YES_NO.includes(row.aws_cert_in_onprem))
    errors.push('"aws_cert_in_onprem" must be yes or no')

  if (row.ssm_parameter === 'yes') {
    if (!row.ssm_format)
      errors.push('"ssm_format" required when ssm_parameter=yes')
    else if (!VALID_SSM_FORMATS.includes(row.ssm_format))
      errors.push(`"ssm_format" must be: ${VALID_SSM_FORMATS.join(', ')}`)

    // ssm_cert_content_type and ssm_cert_param_type are auto-defaulted; only validate if explicitly provided
    if (row.ssm_cert_content_type && !VALID_CONTENT_TYPES.includes(row.ssm_cert_content_type))
      errors.push(`"ssm_cert_content_type" must be: ${VALID_CONTENT_TYPES.join(', ')}`)

    if (row.ssm_cert_param_type && !VALID_PARAM_TYPES.includes(row.ssm_cert_param_type))
      errors.push(`"ssm_cert_param_type" must be: ${VALID_PARAM_TYPES.join(', ')}`)

    // PFX: cert param type must be Advanced only
    if (row.ssm_format === 'PFX' && row.ssm_cert_param_type && row.ssm_cert_param_type !== 'Advanced')
      errors.push('"ssm_cert_param_type" must be "Advanced" for PFX format')

    // PEM: chain param type must be Advanced only
    if (row.ssm_format === 'PEM' && row.ssm_chain_param_type && row.ssm_chain_param_type !== 'Advanced')
      errors.push('"ssm_chain_param_type" must be "Advanced" for PEM format')
  }

  if (row.sm_parameter === 'yes') {
    if (!row.sm_format)
      errors.push('"sm_format" required when sm_parameter=yes')
    else if (!VALID_SM_FORMATS.includes(row.sm_format))
      errors.push(`"sm_format" must be: ${VALID_SM_FORMATS.join(', ')}`)
  }

  return { errors, warnings, isValid: errors.length === 0 }
}

// Post-enrichment validation for on-prem rows (after Venafi data has been merged)
const validateRowPostEnrichment = (row) => {
  const errors = []
  const warnings = []

  // After Venafi enrichment, these fields should now be present
  if (!row.common_name)         errors.push('"common_name" is required')
  if (!row.product_code)        errors.push('"product_code" is required')
  if (!row.product_name)        errors.push('"product_name" is required')

  // Venafi-populated fields — warn if still missing
  if (!row.environment)         warnings.push('"environment" not available from Venafi — please verify')
  if (!row.venafi_policy_base)  warnings.push('"venafi_policy_base" could not be detected — please verify')

  // Enum validations on enriched values
  if (row.environment && !VALID_ENVIRONMENTS.includes(row.environment))
    errors.push(`"environment" must be one of: ${VALID_ENVIRONMENTS.join(', ')}`)
  if (row.venafi_policy_base && !VALID_POLICY_BASES.includes(row.venafi_policy_base))
    errors.push(`"venafi_policy_base" must be one of: ${VALID_POLICY_BASES.join(', ')}`)

  // Defaults already applied, but validate if present
  if (row.mutual_auth && !VALID_YES_NO.includes(row.mutual_auth))
    errors.push('"mutual_auth" must be yes or no')
  if (row.dependency_present && !VALID_TRUE_FALSE.includes(row.dependency_present))
    errors.push('"dependency_present" must be true or false')

  // Conditional
  if (row.mutual_auth === 'yes' && !row.layer7)
    errors.push('"layer7" is required when mutual_auth=yes')
  if (row.layer7 === 'yes' && !row.layer7_endpoint)
    errors.push('"layer7_endpoint" is required when layer7=yes')
  if (row.dependency_present === 'true' && !row.dependencies)
    errors.push('"dependencies" required when dependency_present=true')
  if (row.mutual_auth === 'yes') {
    if (!row.public_cert_key_location)
      errors.push('"public_cert_key_location" required when mutual_auth=yes')
    if (!row.private_cert_key_location)
      errors.push('"private_cert_key_location" required when mutual_auth=yes')
  }

  return { errors, warnings, isValid: errors.length === 0 }
}

// ─── CSV Parser ───────────────────────────────────────────────────────────────

const parseCSV = (text) => {
  const lines = text.replace(/\r\n/g, '\n').replace(/\r/g, '\n').split('\n').filter(l => l.trim() !== '')
  if (lines.length < 2) return { headers: [], rows: [] }

  const parseRow = (line) => {
    const result = []
    let current = ''
    let inQuotes = false
    for (let i = 0; i < line.length; i++) {
      const ch = line[i]
      if (ch === '"') {
        if (inQuotes && line[i + 1] === '"') { current += '"'; i++ }
        else inQuotes = !inQuotes
      } else if (ch === ',' && !inQuotes) {
        result.push(current.trim())
        current = ''
      } else {
        current += ch
      }
    }
    result.push(current.trim())
    return result
  }

  const headers = parseRow(lines[0]).map(h => h.toLowerCase().replace(/\s+/g, '_'))
  const rows = lines.slice(1).map(line => {
    const values = parseRow(line)
    const obj = {}
    headers.forEach((h, i) => { obj[h] = values[i] || '' })
    return obj
  })

  return { headers, rows }
}

// ─── Dropdown definitions for XLSX data validation ──────────────────────────

const DROPDOWN_DEFS = {
  mutual_auth:              '"yes,no"',
  layer7:                   '"yes,no"',
  dependency_present:       '"true,false"',
  environment:              '"dev,qa,uat,prd"',
  certificate_usage:        '"server-internal,server-public,client-internal,client-broadridge,client-external,other"',
  venafi_policy_base:       '"prod-digicert,prod-private,nonprod-digicert,nonprod-private"',
  aws_cert_in_onprem:       '"yes,no"',
  acm_parameter:            '"yes,no"',
  ssm_parameter:            '"yes,no"',
  sm_parameter:             '"yes,no"',
  sm_format:                '"PEM,PFX,JKS"',
}

// ─── Dropdown option arrays (for the manual entry table) ─────────────────────

const DROPDOWN_OPTIONS = {
  mutual_auth:               ['yes', 'no'],
  layer7:                    ['yes', 'no'],
  dependency_present:        ['true', 'false'],
  environment:               ['dev', 'qa', 'uat', 'prd'],
  certificate_usage:         ['server-internal', 'server-public', 'client-internal', 'client-broadridge', 'client-external', 'other'],
  venafi_policy_base:        ['prod-digicert', 'prod-private', 'nonprod-digicert', 'nonprod-private'],
  aws_cert_in_onprem:        ['yes', 'no'],
  acm_parameter:             ['yes', 'no'],
  ssm_parameter:             ['yes', 'no'],
  sm_parameter:              ['yes', 'no'],
  ssm_format:                ['PEM', 'PFX'],
  sm_format:                 ['PEM', 'PFX', 'JKS'],
}

// ─── Column index (1-based) → Excel letter (A, B, …, Z, AA, AB, …) ──────────
const colNumToLetter = (n) => {
  let s = ''
  while (n > 0) {
    const r = (n - 1) % 26
    s = String.fromCharCode(65 + r) + s
    n = Math.floor((n - 1) / 26)
  }
  return s
}

// ─── Build raw XLSX dataValidations XML for injection ────────────────────────
// ExcelJS browser bundle strips dataValidation write support, so we inject
// the XML directly into the sheet ZIP entry using JSZip after generation.
const buildDataValidationsXml = (cols) => {
  const entries = cols
    .map((col, idx) => {
      const formula = DROPDOWN_DEFS[col.key]
      if (!formula) return null
      const colLetter = colNumToLetter(idx + 1)
      const sqref    = `${colLetter}2:${colLetter}1000`
      const errMsg   = formula.replace(/"/g, '')
      return (
        `<dataValidation type="list" allowBlank="1" showDropDown="0"` +
        ` showErrorMessage="1" errorStyle="warning"` +
        ` errorTitle="Suggested values" error="Valid options: ${errMsg}"` +
        ` sqref="${sqref}"><formula1>${formula}</formula1></dataValidation>`
      )
    })
    .filter(Boolean)
  if (entries.length === 0) return ''
  return `<dataValidations count="${entries.length}">${entries.join('')}</dataValidations>`
}

// ─── XLSX Template Generator ──────────────────────────────────────────────────

const AWS_SAMPLE_ROW = [
  'app.broadridge.com', 'BRCC-APP-001', 'brcc-app-prod-aws', '123456789012',
  'app-uuid-1234-abcd-5678', 'prd', 'server-internal', '', 'prod-digicert',
  'san1.broadridge.com|san2.broadridge.com', 'no', '', '', '',
  'false', '', 'no', 'yes', '', '', 'yes', 'PEM',
  '/venafi/certificates/app.broadridge.com/cert', 'SecureString', 'Standard',
  '/venafi/certificates/app.broadridge.com/key', 'SecureString', 'Standard',
  '/venafi/certificates/app.broadridge.com/chain', 'String', 'Standard',
  '', '', '', 'no', '', '', '', '', '',
]

const ONPREM_SAMPLE_ROW = [
  'app.broadridge.com', 'BRCC-APP-001', 'My Application', 'prd', '',
]

const generateXLSXTemplate = async (deploymentType) => {
  const cols = deploymentType === 'aws' ? AWS_COLUMNS : ONPREM_COLUMNS
  const sampleValues = deploymentType === 'aws' ? AWS_SAMPLE_ROW : ONPREM_SAMPLE_ROW

  const workbook = new ExcelJS.Workbook()
  workbook.creator = 'CertManage'
  const ws = workbook.addWorksheet('Certificates', {
    views: [{ state: 'frozen', ySplit: 1 }],
  })

  // ── Columns
  ws.columns = cols.map(c => ({
    header: c.key,
    key: c.key,
    width: Math.max(c.key.length + 4, 22),
  }))

  // ── Header row styling
  const headerRow = ws.getRow(1)
  headerRow.height = 28
  headerRow.eachCell((cell, colNum) => {
    const col = cols[colNum - 1]
    cell.font = { bold: true, color: { argb: 'FFFFFFFF' }, size: 10 }
    cell.fill = {
      type: 'pattern', pattern: 'solid',
      fgColor: { argb: col?.required ? 'FFC62828' : 'FF1565C0' },
    }
    cell.alignment = { vertical: 'middle', horizontal: 'center', wrapText: false }
    cell.border = { bottom: { style: 'medium', color: { argb: 'FFFFFFFF' } } }
  })

  // ── Sample row (row 2)
  const sampleRow = ws.addRow(
    cols.reduce((obj, col, i) => { obj[col.key] = sampleValues[i] ?? ''; return obj }, {})
  )
  sampleRow.height = 20
  sampleRow.eachCell(cell => {
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFF9C4' } }
    cell.font = { italic: true, color: { argb: 'FF5D4037' }, size: 10 }
    cell.border = { bottom: { style: 'thin', color: { argb: 'FFBBBBBB' } } }
    cell.alignment = { vertical: 'middle' }
  })

  // ── Instructions sheet
  const info = workbook.addWorksheet('Instructions')
  info.columns = [
    { header: 'Column', key: 'col', width: 30 },
    { header: 'Required', key: 'req', width: 12 },
    { header: 'Description', key: 'desc', width: 70 },
    { header: 'Valid Values / Dropdown', key: 'vals', width: 80 },
  ]
  const infoHeader = info.getRow(1)
  infoHeader.height = 22
  infoHeader.eachCell(cell => {
    cell.font = { bold: true, color: { argb: 'FFFFFFFF' }, size: 10 }
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF37474F' } }
    cell.alignment = { vertical: 'middle', horizontal: 'center' }
  })
  cols.forEach(c => {
    const row = info.addRow({
      col: c.key,
      req: c.required ? 'Required' : 'Optional',
      desc: c.description,
      vals: DROPDOWN_DEFS[c.key] ? DROPDOWN_DEFS[c.key].replace(/"/g, '') : '(free text)',
    })
    row.getCell('req').font = { color: { argb: c.required ? 'FFC62828' : 'FF546E7A' }, bold: c.required }
    row.getCell('col').font = { name: 'Courier New', size: 9 }
    row.height = 18
  })

  // ── Generate base XLSX buffer via ExcelJS (styling, columns, sample row)
  const baseBuffer = await workbook.xlsx.writeBuffer()

  // ── Inject dataValidations XML using JSZip (ExcelJS browser build omits this)
  // Per OOXML spec, <dataValidations> MUST appear immediately after </sheetData>
  const zip = await JSZip.loadAsync(baseBuffer)
  const sheetXml = await zip.file('xl/worksheets/sheet1.xml').async('string')
  const dvXml = buildDataValidationsXml(cols)
  const patchedXml = dvXml
    ? sheetXml.replace('</sheetData>', `</sheetData>${dvXml}`)
    : sheetXml
  zip.file('xl/worksheets/sheet1.xml', patchedXml)
  const finalBuffer = await zip.generateAsync({ type: 'arraybuffer', compression: 'DEFLATE' })

  // ── Download
  const blob = new Blob([finalBuffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = `bulk_upload_${deploymentType}_template.xlsx`
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(url)
}

// ─── XLSX Parser ─────────────────────────────────────────────────────────────

const parseXLSXFile = async (file) => {
  const workbook = new ExcelJS.Workbook()
  const buffer = await file.arrayBuffer()
  await workbook.xlsx.load(buffer)
  const ws = workbook.worksheets[0]
  if (!ws) return { rows: [] }

  let headers = []
  const rows = []
  ws.eachRow((row, rowNumber) => {
    // row.values is 1-indexed; index 0 is always undefined in exceljs
    const values = row.values.slice(1)
    if (rowNumber === 1) {
      headers = values.map(v => String(v ?? '').trim().toLowerCase().replace(/\s+/g, '_'))
      return
    }
    // Skip completely empty rows and the yellow sample row if user left it
    if (values.every(v => v === null || v === undefined || String(v).trim() === '')) return
    const obj = {}
    headers.forEach((h, i) => {
      const raw = values[i]
      obj[h] = raw !== null && raw !== undefined ? String(raw).trim() : ''
    })
    rows.push(obj)
  })
  return { rows }
}

// ─── Row to API payload ────────────────────────────────────────────────────────

const rowToPayload = (row, deploymentType, emailId) => {
  const dependencies = row.dependencies
    ? row.dependencies.split('|').map(d => d.trim()).filter(Boolean)
    : []

  const payload = {
    common_name:       row.common_name,
    product_code:      row.product_code,
    product_name:      row.product_name || '',
    environment:       row.environment,
    email_id:          emailId,
    pre_existing:      'true',
    is_aws:            deploymentType === 'aws' ? 'true' : 'false',
    certificate_usage: null,
    itsm_group:        row.itsm_group || '',
    venafi_policy_base:row.venafi_policy_base,
    san_entries:       [],
    mutual_auth:       row.mutual_auth || 'no',
    layer7:            row.layer7 || 'no',
    layer7_endpoint:   row.layer7_endpoint || null,
    client_involvement:row.client_name ? 'true' : 'false',
    client_name:       row.client_name || null,
    dependency_present:row.dependency_present || 'false',
    dependencies,
    aws_additional_fields: deploymentType === 'aws' ? {
      accountName:            row.account_name,
      accountNumber:          row.account_number,
      applicationId:          row.application_id,
      awsCertInOnprem:        row.aws_cert_in_onprem || 'no',
      acmParameter:           row.acm_parameter || 'no',
      existingAcmArn1:        row.existing_acm_arn1 || '',
      existingAcmArn2:        row.existing_acm_arn2 || '',
      ssmParameter:           row.ssm_parameter || 'no',
      ssmFormat:              row.ssm_format || '',
      ssmCertPath:            row.ssm_cert_path || (row.common_name && row.environment && row.ssm_format ? `/venafi/certificates/${row.environment.toLowerCase()}/${row.common_name}/${row.ssm_format.toLowerCase()}/cert` : ''),
      ssmCertContentType:     row.ssm_cert_content_type || 'String',
      ssmCertParamType:       row.ssm_cert_param_type || (row.ssm_format === 'PFX' ? 'Advanced' : 'Standard'),
      ssmPrivateKeyPath:      row.ssm_private_key_path || (row.ssm_format === 'PEM' && row.common_name && row.environment ? `/venafi/certificates/${row.environment.toLowerCase()}/${row.common_name}/pem/key` : ''),
      ssmKeyContentType:      row.ssm_key_content_type || (row.ssm_format === 'PEM' ? 'String' : ''),
      ssmKeyParamType:        row.ssm_key_param_type || (row.ssm_format === 'PEM' ? 'Standard' : ''),
      ssmChainKeyPath:        row.ssm_chain_key_path || (row.ssm_format === 'PEM' && row.common_name && row.environment ? `/venafi/certificates/${row.environment.toLowerCase()}/${row.common_name}/pem/chain` : ''),
      ssmChainContentType:    row.ssm_chain_content_type || (row.ssm_format === 'PEM' ? 'String' : ''),
      ssmChainParamType:      row.ssm_chain_param_type || (row.ssm_format === 'PEM' ? 'Advanced' : ''),
      ssmCertPasswordPath:    row.ssm_cert_password_path || (row.ssm_format === 'PFX' && row.common_name && row.environment ? `/venafi/certificates/${row.environment.toLowerCase()}/${row.common_name}/pfx/password` : ''),
      ssmPasswordContentType: row.ssm_password_content_type || (row.ssm_format === 'PFX' ? 'String' : ''),
      ssmPasswordParamType:   row.ssm_password_param_type || (row.ssm_format === 'PFX' ? 'Standard' : ''),
      smParameter:            row.sm_parameter || 'no',
      smFormat:               row.sm_format || '',
      smCertPath:             row.sm_cert_path || (row.common_name && row.environment ? `/venafi/certificates/${row.environment.toLowerCase()}/${row.common_name}` : ''),
    } : null,
  }

  return payload
}

// ─── Sub-components ────────────────────────────────────────────────────────────

const StatusChip = ({ row }) => {
  if (!row._validation) return null
  if (row._validation.errors.length > 0)
    return <Chip icon={<ErrorIcon />} label="Error" color="error" size="small" variant="filled" />
  if (row._validation.warnings.length > 0)
    return <Chip icon={<WarningIcon />} label="Warning" color="warning" size="small" variant="outlined" />
  return <Chip icon={<CheckCircleIcon />} label="Valid" color="success" size="small" variant="outlined" />
}

const SubmitStatusChip = ({ status }) => {
  if (status === 'pending')           return <Chip label="Pending" color="default" size="small" />
  if (status === 'running')           return <Chip label="Submitting..." color="info" size="small" />
  if (status === 'success')           return <Chip icon={<CheckCircleIcon />} label="Success" color="success" size="small" />
  if (status === 'error')             return <Chip icon={<ErrorIcon />} label="Failed" color="error" size="small" />
  if (status === 'venafi_pick_needed') return <Chip icon={<ErrorIcon />} label="Awaiting Selection" color="warning" size="small" />
  return null
}

// ─── Account Select with inline search dropdown ──────────────────────────────
const getSsmDefault = (key, row) => {
  const fmt = row.ssm_format || ''
  const defaults = {
    ssm_cert_content_type:     fmt ? 'String' : '',
    ssm_cert_param_type:       fmt === 'PFX' ? 'Advanced' : fmt === 'PEM' ? 'Standard' : '',
    ssm_key_content_type:      fmt === 'PEM' ? 'String' : '',
    ssm_key_param_type:        fmt === 'PEM' ? 'Standard' : '',
    ssm_chain_content_type:    fmt === 'PEM' ? 'String' : '',
    ssm_chain_param_type:      fmt === 'PEM' ? 'Advanced' : '',
    ssm_password_content_type: fmt === 'PFX' ? 'String' : '',
    ssm_password_param_type:   fmt === 'PFX' ? 'Standard' : '',
  }
  return defaults[key] || ''
}

const AccountSelectCell = ({ value, onChange, options, noOptionsText = 'No options found', placeholder = 'Select...' }) => {
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')
  const anchorRef = useRef(null)

  const filtered = options.filter(opt =>
    opt.account_name.toLowerCase().includes(search.toLowerCase())
  )

  const handleSelect = (opt) => {
    onChange(opt)
    setOpen(false)
    setSearch('')
  }

  const handleClear = (e) => {
    e.stopPropagation()
    onChange(null)
    setSearch('')
  }

  return (
    <ClickAwayListener onClickAway={() => { setOpen(false); setSearch('') }}>
      <Box>
        <Box
          ref={anchorRef}
          onClick={() => setOpen(prev => !prev)}
          sx={{
            minWidth: 220,
            height: 36,
            border: '1px solid',
            borderColor: open ? 'primary.main' : 'rgba(0,0,0,0.23)',
            borderRadius: 1,
            px: 1.5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            cursor: 'pointer',
            bgcolor: 'background.paper',
            userSelect: 'none',
            '&:hover': { borderColor: 'text.primary' },
          }}
        >
          <Typography
            variant="body2"
            sx={{ fontSize: '0.8rem', color: value ? 'text.primary' : 'text.disabled', flexGrow: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
          >
            {value ? `${value.account_name}${value.product_env ? ` (${value.product_env.toUpperCase()})` : ''}` : placeholder}
          </Typography>
          {value ? (
            <IconButton size="small" onClick={handleClear} sx={{ p: 0.25, ml: 0.5 }}>
              <span style={{ fontSize: '0.9rem', lineHeight: 1, color: '#888' }}>✕</span>
            </IconButton>
          ) : (
            <span style={{ fontSize: '0.65rem', color: '#888', marginLeft: 4 }}>▼</span>
          )}
        </Box>
        <Popper open={open} anchorEl={anchorRef.current} placement="bottom-start" sx={{ zIndex: 1300, minWidth: 260 }}>
          <Paper elevation={4} sx={{ mt: 0.5, border: '1px solid', borderColor: 'divider', borderRadius: 1, overflow: 'hidden' }}>
            <Box sx={{ p: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
              <TextField
                autoFocus
                size="small"
                fullWidth
                placeholder="Search account..."
                value={search}
                onChange={e => setSearch(e.target.value)}
                onClick={e => e.stopPropagation()}
                sx={{ '& input': { fontSize: '0.8rem' } }}
              />
            </Box>
            <Box sx={{ maxHeight: 240, overflowY: 'auto' }}>
              {filtered.length === 0 ? (
                <Box sx={{ px: 2, py: 1.5, color: 'text.secondary', fontSize: '0.8rem' }}>{noOptionsText}</Box>
              ) : (
                filtered.map(opt => (
                  <Box
                    key={`${opt.account_number}-${opt.product_env}`}
                    onClick={() => handleSelect(opt)}
                    sx={{
                      px: 2, py: 1, fontSize: '0.8rem', cursor: 'pointer',
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1,
                      bgcolor: value?.account_number === opt.account_number && value?.product_env === opt.product_env ? 'primary.50' : 'transparent',
                      '&:hover': { bgcolor: 'action.hover' },
                    }}
                  >
                    <span>{opt.account_name}</span>
                    {opt.product_env && (
                      <Chip label={opt.product_env.toUpperCase()} size="small" color="primary" variant="outlined" sx={{ fontSize: '0.65rem', height: 18 }} />
                    )}
                  </Box>
                ))
              )}
            </Box>
          </Paper>
        </Popper>
      </Box>
    </ClickAwayListener>
  )
}

// ─── Memoized table row ──────────────────────────────────────────────────────

const ManualTableRow = React.memo(function ManualTableRow({ row, rowIdx, cols, updateManualRow, onDelete, allAwsAccounts, flatAccounts, itsmGroupOptions, onpremProductOptions, onAcmLookup, isBlocked, isNotOnboarded, isTstAmbiguous, tstAccounts }) {
  const anyBlocked = isBlocked || isNotOnboarded
  const blockedSx = anyBlocked ? { pointerEvents: 'none', opacity: 0.45 } : {}
  return (
    <TableRow hover sx={anyBlocked ? { bgcolor: 'grey.100' } : isTstAmbiguous ? { bgcolor: 'warning.50', outline: '1px solid', outlineColor: 'warning.200' } : {}}>
      <TableCell sx={{ position: 'sticky', left: 0, bgcolor: anyBlocked ? 'grey.100' : isTstAmbiguous ? '#fff8e1' : 'background.paper', zIndex: 1, color: 'text.secondary', fontSize: '0.75rem', textAlign: 'center' }}>
        {rowIdx + 1}
        {isTstAmbiguous && (
          <Tooltip title="Common name has no 'qa'/'uat' signal. Update the common name or select a TST account to reveal all fields." arrow>
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 0.25 }}>
              <WarningIcon sx={{ fontSize: 22, color: '#f57c00', filter: 'drop-shadow(0 0 3px rgba(245,124,0,0.6))', cursor: 'help' }} />
            </Box>
          </Tooltip>
        )}
      </TableCell>
      {cols.map(col => {
        // For TST-ambiguous rows, hide every column except common_name and account_name
        if (isTstAmbiguous && col.key !== 'common_name' && col.key !== 'account_name') {
          return (
            <TableCell key={col.key} sx={{ py: 0.5, px: 0.5 }}>
              <Box sx={{ minWidth: col.key === 'itsm_group' ? 160 : 80, height: 30, bgcolor: 'grey.100', borderRadius: 1, border: '1px dashed', borderColor: 'grey.300' }} />
            </TableCell>
          )
        }
        return (
        <TableCell key={col.key} sx={{ py: 0.5, px: 0.5, ...(anyBlocked && col.key !== 'common_name' ? blockedSx : {}) }}>
          {col.key === 'account_name' ? (
            <AccountSelectCell
              value={flatAccounts.find(a => a.account_name === row.account_name && a.product_env === row.environment) || null}
              options={isTstAmbiguous ? tstAccounts : flatAccounts}
              onChange={newVal => {
                updateManualRow(row._id, 'account_name', newVal ? newVal.account_name : '')
                updateManualRow(row._id, 'account_number', newVal ? newVal.account_number : '')
                updateManualRow(row._id, 'application_id', newVal ? newVal.application_id || '' : '')
                updateManualRow(row._id, 'product_code', newVal ? newVal.product_code || '' : '')
                updateManualRow(row._id, 'environment', newVal?.product_env || '')
              }}
              placeholder="Select account..."
              noOptionsText="No onboarded accounts found"
            />
          ) : col.key === 'itsm_group' ? (
            <AccountSelectCell
              value={row.itsm_group ? { account_name: row.itsm_group, account_number: row.itsm_group } : null}
              options={itsmGroupOptions}
              onChange={newVal => updateManualRow(row._id, 'itsm_group', newVal ? newVal.account_name : '')}
              placeholder="Select ITSM group..."
              noOptionsText="No ITSM groups found"
            />
          ) : col.key === 'common_name' ? (
            <TextField
              size="small"
              value={row[col.key] || ''}
              onChange={e => updateManualRow(row._id, col.key, e.target.value)}
              onBlur={e => { if (e.target.value.trim() && onAcmLookup) onAcmLookup(row._id, e.target.value.trim()) }}
              placeholder="required"
              sx={{ minWidth: 200, '& input': { fontSize: '0.8rem', py: '6px' } }}
            />
          ) : col.key === 'product_name' ? (
            <AccountSelectCell
              value={onpremProductOptions.find(p => p.account_name === row.product_name) || null}
              options={onpremProductOptions}
              onChange={newVal => {
                updateManualRow(row._id, 'product_name', newVal ? newVal.account_name : '')
                updateManualRow(row._id, 'product_code', newVal ? newVal.account_number : '')
              }}
              placeholder="Select product..."
              noOptionsText="No products found"
            />
          ) : col.key === 'account_number' || col.key === 'application_id' ? (
            <TextField
              size="small"
              value={row[col.key] || ''}
              placeholder="auto-filled"
              InputProps={{ readOnly: true }}
              sx={{ minWidth: 140, '& input': { fontSize: '0.8rem', py: '6px', color: 'text.secondary', bgcolor: 'grey.50' } }}
            />
          ) : col.key === 'product_code' ? (
            <TextField
              size="small"
              value={row[col.key] || ''}
              placeholder="auto-filled"
              InputProps={{ readOnly: true }}
              sx={{ minWidth: 140, '& input': { fontSize: '0.8rem', py: '6px', color: 'text.secondary', bgcolor: 'grey.50' } }}
            />
          ) : col.key === 'ssm_cert_path' || col.key === 'ssm_private_key_path' || col.key === 'ssm_chain_key_path' || col.key === 'ssm_cert_password_path' || col.key === 'sm_cert_path' ? (() => {
              const sdlc = row.environment ? row.environment.toLowerCase() : '<env>'
              const cn = row.common_name || ''
              const ssmFmt = (row.ssm_format || '').toLowerCase()
              const pathMap = {
                ssm_cert_path:          cn && ssmFmt ? `/venafi/certificates/${sdlc}/${cn}/${ssmFmt}/cert` : '',
                ssm_private_key_path:   cn && ssmFmt === 'pem' ? `/venafi/certificates/${sdlc}/${cn}/pem/key` : '',
                ssm_chain_key_path:     cn && ssmFmt === 'pem' ? `/venafi/certificates/${sdlc}/${cn}/pem/chain` : '',
                ssm_cert_password_path: cn && ssmFmt === 'pfx' ? `/venafi/certificates/${sdlc}/${cn}/pfx/password` : '',
                sm_cert_path:           cn ? `/venafi/certificates/${sdlc}/${cn}` : '',
              }
              const computed = pathMap[col.key] || ''
              return (
                <TextField
                  size="small"
                  value={row[col.key] || computed}
                  onChange={e => updateManualRow(row._id, col.key, e.target.value)}
                  placeholder="auto-filled from common name"
                  sx={{ minWidth: 300, '& input': { fontSize: '0.8rem', py: '6px', color: row[col.key] ? 'text.primary' : 'text.secondary' } }}
                />
              )
            })() : DROPDOWN_OPTIONS[col.key] ? (() => {
              const isFormatCol = col.key === 'ssm_format' || col.key === 'sm_format'
              const parentKey = col.key === 'ssm_format' ? 'ssm_parameter' : col.key === 'sm_format' ? 'sm_parameter' : null
              const isEnvSetByAccount = col.key === 'environment' && !!row.account_name
              const isAutoFilled = row._autoFilledFields?.includes(col.key)
              const isLayer7 = col.key === 'layer7'
              const isDisabled = (isFormatCol && row[parentKey] !== 'yes') || isEnvSetByAccount || isAutoFilled || isLayer7
              const dropdownOptions = DROPDOWN_OPTIONS[col.key]
              return (
                <FormControl size="small" sx={{ minWidth: 120 }}>
                  <Select
                    value={isEnvSetByAccount ? (row[col.key] || '') : isDisabled ? (row[col.key] || '') : (row[col.key] || getSsmDefault(col.key, row))}
                    onChange={e => updateManualRow(row._id, col.key, e.target.value)}
                    displayEmpty
                    disabled={isDisabled}
                    sx={{ fontSize: '0.8rem', ...((isEnvSetByAccount || isAutoFilled) && { bgcolor: 'grey.50', '& .MuiSelect-select': { color: 'text.secondary' } }) }}
                  >
                    <MenuItem value=""><em style={{ color: '#aaa' }}>—</em></MenuItem>
                    {dropdownOptions.map(opt => (
                      <MenuItem key={opt} value={opt} sx={{ fontSize: '0.8rem' }}>{opt}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )
            })() : (
            <TextField
              size="small"
              value={row[col.key] || ''}
              onChange={e => updateManualRow(row._id, col.key, e.target.value)}
              placeholder={col.required ? 'required' : ''}
              InputProps={{ readOnly: !!row._autoFilledFields?.includes(col.key) }}
              sx={{ minWidth: 160, '& input': { fontSize: '0.8rem', py: '6px', ...(row._autoFilledFields?.includes(col.key) && { color: 'text.secondary', bgcolor: 'grey.50' }) } }}
            />
          )}
        </TableCell>
        )
      })}
      <TableCell sx={{ py: 0.5, ...(isBlocked ? blockedSx : {}) }}>
        <IconButton size="small" color="error" onClick={() => onDelete(row._id)} disabled={isBlocked}>
          <DeleteIcon fontSize="small" />
        </IconButton>
      </TableCell>
    </TableRow>
  )
})

// ─── Main Component ────────────────────────────────────────────────────────────

const AWS_STEPS    = ['Select Type', 'Download Template', 'Add Data', 'Review & Validate', 'Submit']
const ONPREM_STEPS = ['Select Type', 'Download Template', 'Add Data', 'Venafi Lookup', 'Review & Validate', 'Submit']

const BulkCertificateUpload = () => {
  const [activeStep, setActiveStep]         = useState(0)
  const [deploymentType, setDeploymentType] = useState('')   // 'aws' | 'onprem'
  const [parsedRows, setParsedRows]         = useState([])
  const [parseError, setParseError]         = useState('')
  const [fileName, setFileName]             = useState('')
  const [expandedRows, setExpandedRows]     = useState({})
  const [submitting, setSubmitting]         = useState(false)
  const [submitResults, setSubmitResults]   = useState([]) // per-row {status, message}
  const [submitProgress, setSubmitProgress] = useState(0)
  const [columnDialogOpen, setColumnDialogOpen] = useState(false)
  const [snackbar, setSnackbar]             = useState({ open: false, message: '', severity: 'success', key: 0 })
  // Rows blocked due to TST account missing QA/UAT onboarding: Set of row _ids
  const [tstBlockedRows, setTstBlockedRows] = useState(new Set())
  // Rows blocked due to account not being onboarded at all: Set of row _ids
  const [notOnboardedRows, setNotOnboardedRows] = useState(new Set())
  // TST rows where CN has no qa/uat — maps rowId → [tst flatAccount matches] for filtered dropdown
  const [tstAmbiguousMap, setTstAmbiguousMap] = useState(new Map())
  const [dragOver, setDragOver]             = useState(false)

  // ─── Venafi lookup state (on-prem only) ──────────────────────────────────
  const [venafiLookupStatus, setVenafiLookupStatus]   = useState('idle') // 'idle' | 'running' | 'done'
  const [venafiLookupProgress, setVenafiLookupProgress] = useState(0)
  const [venafiMatches, setVenafiMatches]             = useState({})   // { [rowId]: { matches: [], status, error, selectedMatch, details } }
  const [certSelectionDialog, setCertSelectionDialog] = useState({ open: false, rowId: null }) // for multi-match picker
  // CA selection dialog — shown when a common name maps to multiple CAs in the inventory
  const [caSelectDialog, setCaSelectDialog] = useState({ open: false, rowId: null, commonName: '', entries: [], selectedIdx: 0 })
  // InUse=false ARN selection dialog — shown when all ACM ARNs for a CN have InUse=false
  const [arnSelectDialog, setArnSelectDialog] = useState({ open: false, rowId: null, commonName: '', entries: [], selectedIdx: 0 })
  // Venafi multi-match: map from rowIdx → { commonName, matches, payload }
  const [venafiPickItems, setVenafiPickItems] = useState({})
  // Venafi pick dialog — opened manually via button on each pending row
  const [venafiPickDialog, setVenafiPickDialog] = useState({ open: false, rowIdx: null, selectedIdx: 0 })

  // Duplicate common_name resolution dialog
  const [duplicateDialog, setDuplicateDialog] = useState({ open: false, groups: [], source: '', allRows: [] })
  // groups: [{ commonName, rows: [...], selectedId }]  source: 'manual' | 'file'  allRows: full row list
  const fileInputRef = useRef(null)
  const manualRowIdRef = useRef(0)
  const [entryMode, setEntryMode] = useState('manual')   // 'file' | 'manual'
  const [manualRows, setManualRows] = useState([])
  const [allAwsAccounts, setAllAwsAccounts] = useState([])
  const [itsmGroups, setItsmGroups] = useState([])
  const [onpremProducts, setOnpremProducts] = useState([])

  const flattenedAccounts = useMemo(() =>
    allAwsAccounts.flatMap(acct =>
      (acct.onboarded_environments?.length ? acct.onboarded_environments : [acct.auto_environment].filter(Boolean))
        .map(env => ({ ...acct, product_env: env }))
    ), [allAwsAccounts])

  // Keep a stable ref so handleAcmLookup (useCallback []) can read the latest list
  const flattenedAccountsRef = useRef([])
  useEffect(() => { flattenedAccountsRef.current = flattenedAccounts }, [flattenedAccounts])

  // Auto-close the error snackbar when all blocks are resolved
  useEffect(() => {
    if (tstBlockedRows.size === 0 && notOnboardedRows.size === 0) {
      setSnackbar(s => s.severity === 'error' ? { ...s, open: false } : s)
    }
  }, [tstBlockedRows, notOnboardedRows])

  useEffect(() => {
    awsCertAccountApi.getAllOnboardedAccounts()
      .then(res => setAllAwsAccounts(res.data.items || []))
      .catch(() => setAllAwsAccounts([]))
    certificateApi.getItsmGroups()
      .then(res => setItsmGroups(res.data.groups || []))
      .catch(() => setItsmGroups([]))
    itsmProductApi.getOnboardedOnprem()
      .then(res => setOnpremProducts(res.data.items || []))
      .catch(() => setOnpremProducts([]))
  }, [])

  const itsmGroupOptions = useMemo(() => itsmGroups.map(g => ({ account_name: g, account_number: g })), [itsmGroups])
  const onpremProductOptions = useMemo(() => onpremProducts.map(p => ({ account_name: p.product_name, account_number: p.product_code })), [onpremProducts])

  const emailId = localStorage.getItem('mail') || ''

  const handleDeploymentSelect = (type) => {
    if (type !== deploymentType) {
      setManualRows([])
      setParsedRows([])
      setParseError('')
      setFileName('')
      setEntryMode('manual')
      setVenafiLookupStatus('idle')
      setVenafiLookupProgress(0)
      setVenafiMatches({})
      setSubmitResults([])
      manualRowIdRef.current = 0
    }
    setDeploymentType(type)
  }

  const processFile = useCallback(async (file) => {
    if (!file) return
    const isCSV  = file.name.toLowerCase().endsWith('.csv')
    if (!isCSV) {
      setParseError('Please upload a .csv file.')
      return
    }
    setParseError('')
    setFileName(file.name)

    try {
      let rows
      const text = await file.text()
      const result = parseCSV(text)
      rows = result.rows

      if (rows.length === 0) {
        setParseError('No data rows found. Ensure the file has a header row and at least one data row.')
        setParsedRows([])
        return
      }

      const validated = rows.map((row, idx) => {
        const normalized = normalizeRow(row)
        return {
          ...normalized,
          _id: idx,
          _validation: validateRow(normalized, deploymentType),
          _submitStatus: 'pending',
          _submitMessage: '',
        }
      })

      // Check for duplicate common_names in the uploaded file
      const grouped = {}
      validated.forEach(row => {
        const cn = (row.common_name || '').trim()
        if (!cn) return
        if (!grouped[cn]) grouped[cn] = []
        grouped[cn].push(row)
      })
      const duplicateGroups = Object.entries(grouped)
        .filter(([, r]) => r.length > 1)
        .map(([commonName, r]) => ({ commonName, rows: r, selectedId: r[0]._id }))

      if (duplicateGroups.length > 0) {
        setParsedRows(validated)
        setSubmitResults([])
        setDuplicateDialog({ open: true, groups: duplicateGroups, source: 'file', allRows: validated })
        return
      }

      setParsedRows(validated)
      setSubmitResults([])
      // For on-prem, go to Venafi Lookup step (step 3); for AWS, go to Review (step 3 in AWS_STEPS)
      setActiveStep(3)
    } catch (err) {
      setParseError(`Failed to parse file: ${err.message}`)
      setParsedRows([])
    }
  }, [deploymentType])

  const handleFileInput = (e) => {
    const file = e.target.files[0]
    if (file) processFile(file)
    e.target.value = ''
  }

  const handleDrop = useCallback((e) => {
    e.preventDefault()
    setDragOver(false)
    const file = e.dataTransfer.files[0]
    if (file) processFile(file)
  }, [processFile])

  const handleDragOver = (e) => { e.preventDefault(); setDragOver(true) }
  const handleDragLeave = () => setDragOver(false)

  const toggleRowExpand = (id) => setExpandedRows(prev => ({ ...prev, [id]: !prev[id] }))

  const removeRow = (id) => setParsedRows(prev => prev.filter(r => r._id !== id))

  const STEPS = deploymentType === 'onprem' ? ONPREM_STEPS : AWS_STEPS
  // Step index mapping: on-prem has an extra "Venafi Lookup" step at index 3
  const REVIEW_STEP = deploymentType === 'onprem' ? 4 : 3
  const SUBMIT_STEP = deploymentType === 'onprem' ? 5 : 4

  const validRows    = parsedRows.filter(r => r._validation?.isValid)
  const invalidRows  = parsedRows.filter(r => !r._validation?.isValid)
  const warningRows  = parsedRows.filter(r => r._validation?.isValid && r._validation?.warnings.length > 0)

  // ─── Venafi Lookup Logic (On-Prem) ──────────────────────────────────────────

  const enrichRowFromVenafi = (row, details, match) => {
    const enriched = { ...row }
    // Only fill fields that the user did NOT provide in their CSV
    if (!enriched.environment && details.environment)
      enriched.environment = details.environment.toLowerCase()
    if (!enriched.venafi_policy_base) {
      enriched.venafi_policy_base = detectPolicyBase(match.policy_dn || match.certificate_dn)
    }
    if (!enriched.client_name && details.client_name)
      enriched.client_name = details.client_name
    // Set defaults for fields that weren't provided
    if (!enriched.mutual_auth) enriched.mutual_auth = 'no'
    if (!enriched.dependency_present) enriched.dependency_present = 'false'
    // Store Venafi metadata for display
    enriched._venafiDetails = details
    enriched._venafiMatch = match
    return enriched
  }

  const runVenafiLookup = async () => {
    setVenafiLookupStatus('running')
    setVenafiLookupProgress(0)
    const newMatches = {}
    let done = 0
    const total = parsedRows.length

    // Process rows in batches of 5 to avoid overwhelming the API
    const BATCH_SIZE = 5
    for (let i = 0; i < parsedRows.length; i += BATCH_SIZE) {
      const batch = parsedRows.slice(i, i + BATCH_SIZE)
      const results = await Promise.allSettled(
        batch.map(async (row) => {
          const cn = row.common_name?.trim()
          if (!cn) {
            return { rowId: row._id, status: 'error', error: 'No common_name provided', matches: [] }
          }
          try {
            // Check if already onboarded
            const onboardedResp = await certificateApi.checkOnboarded(cn)
            if (onboardedResp.data?.onboarded) {
              return {
                rowId: row._id,
                status: 'already-onboarded',
                error: `Already onboarded (ID: ${onboardedResp.data.cert_id || 'N/A'})`,
                matches: [],
              }
            }
            // Search Venafi
            const venafiResp = await certificateApi.checkVenafiExists(cn, 25)
            const { exists, matches, truncated } = venafiResp.data || {}
            if (!exists || !matches?.length) {
              return { rowId: row._id, status: 'not-found', error: 'Certificate not found in Venafi', matches: [] }
            }
            return {
              rowId: row._id,
              status: matches.length === 1 ? 'single' : 'multiple',
              matches: matches,
              truncated: Boolean(truncated),
              error: '',
            }
          } catch (err) {
            return {
              rowId: row._id,
              status: 'error',
              error: err?.response?.data?.detail || err?.message || 'Venafi lookup failed',
              matches: [],
            }
          }
        })
      )
      results.forEach((outcome) => {
        if (outcome.status === 'fulfilled') {
          const r = outcome.value
          newMatches[r.rowId] = r
        }
      })
      done += batch.length
      setVenafiLookupProgress(Math.round((done / total) * 100))
      setVenafiMatches({ ...newMatches })
    }

    // Auto-fetch details for single-match rows
    const singleMatchRows = Object.values(newMatches).filter(m => m.status === 'single')
    for (let i = 0; i < singleMatchRows.length; i += BATCH_SIZE) {
      const batch = singleMatchRows.slice(i, i + BATCH_SIZE)
      await Promise.allSettled(
        batch.map(async (m) => {
          const match = m.matches[0]
          try {
            const detResp = await certificateApi.getVenafiCertificateDetails(match.certificate_dn, match.guid)
            newMatches[m.rowId] = { ...newMatches[m.rowId], selectedMatch: match, details: detResp.data || {} }
          } catch (err) {
            newMatches[m.rowId] = {
              ...newMatches[m.rowId],
              status: 'error',
              error: `Found in Venafi but failed to fetch details: ${err?.message || 'unknown error'}`,
            }
          }
        })
      )
      setVenafiMatches({ ...newMatches })
    }

    setVenafiMatches({ ...newMatches })

    // Back-propagate detected venafi_policy_base to manualRows so it's
    // visible/editable if the user navigates back to the manual entry table.
    setManualRows(prev => prev.map(row => {
      if (row.venafi_policy_base) return row  // don't overwrite user-provided value
      const m = newMatches[row._id]
      if (!m || !m.selectedMatch) return row
      const detected = detectPolicyBase(m.selectedMatch.policy_dn || m.selectedMatch.certificate_dn)
      if (!detected) return row
      return { ...row, venafi_policy_base: detected }
    }))

    setVenafiLookupStatus('done')
  }

  const handleSelectVenafiCert = async (rowId, match) => {
    // Update the selection immediately
    setVenafiMatches(prev => ({
      ...prev,
      [rowId]: { ...prev[rowId], selectedMatch: match, details: null, status: 'fetching-details' },
    }))
    try {
      const detResp = await certificateApi.getVenafiCertificateDetails(match.certificate_dn, match.guid)
      setVenafiMatches(prev => ({
        ...prev,
        [rowId]: { ...prev[rowId], details: detResp.data || {}, status: 'multiple' },
      }))
    } catch (err) {
      setVenafiMatches(prev => ({
        ...prev,
        [rowId]: {
          ...prev[rowId],
          status: 'error',
          error: `Failed to fetch certificate details: ${err?.message || 'unknown error'}`,
        },
      }))
    }
    setCertSelectionDialog({ open: false, rowId: null })
  }

  const applyVenafiEnrichmentAndProceed = () => {
    // Merge Venafi data into parsed rows, re-validate, then go to Review step
    const enriched = parsedRows.map(row => {
      const lookupResult = venafiMatches[row._id]
      if (!lookupResult) return row
      if (lookupResult.status === 'already-onboarded') {
        return {
          ...row,
          _validation: { errors: [lookupResult.error], warnings: [], isValid: false },
          _venafiStatus: 'already-onboarded',
        }
      }
      if (lookupResult.status === 'not-found' || lookupResult.status === 'error') {
        return {
          ...row,
          _validation: { errors: [lookupResult.error], warnings: [], isValid: false },
          _venafiStatus: lookupResult.status,
        }
      }
      if (!lookupResult.selectedMatch || !lookupResult.details) {
        return {
          ...row,
          _validation: { errors: ['No Venafi certificate selected'], warnings: [], isValid: false },
          _venafiStatus: 'no-selection',
        }
      }
      // Enrich from Venafi
      const enrichedRow = enrichRowFromVenafi(row, lookupResult.details, lookupResult.selectedMatch)
      const normalized = normalizeRow(enrichedRow)
      return {
        ...normalized,
        _id: row._id,
        _validation: validateRowPostEnrichment(normalized),
        _submitStatus: 'pending',
        _submitMessage: '',
        _venafiStatus: 'enriched',
      }
    })
    setParsedRows(enriched)
    setActiveStep(REVIEW_STEP)
  }

  // Check if all multi-match rows have a selection
  const allMultiMatchesResolved = Object.values(venafiMatches).every(
    m => m.status !== 'multiple' || (m.selectedMatch && m.details)
  )

  const handleSubmitAll = async () => {
    if (validRows.length === 0) return
    setSubmitting(true)
    setSubmitProgress(0)
    setActiveStep(SUBMIT_STEP)

    const results = parsedRows.map(r => ({ ...r, _submitStatus: r._validation.isValid ? 'pending' : 'skipped', _submitMessage: '' }))
    setSubmitResults([...results])

    // Pre-flight: check all valid rows against the DB in parallel to skip already-onboarded certs
    const duplicateChecks = await Promise.allSettled(
      results.map(async (row, i) => {
        if (!row._validation.isValid) return null
        try {
          const resp = await certificateApi.checkOnboarded(row.common_name)
          return resp.data?.onboarded ? i : null
        } catch {
          return null
        }
      })
    )
    duplicateChecks.forEach((outcome, i) => {
      if (outcome.status === 'fulfilled' && outcome.value !== null) {
        results[i] = {
          ...results[i],
          _submitStatus: 'skipped',
          _submitMessage: `Already onboarded — skipped.`,
        }
      }
    })
    setSubmitResults([...results])

    let done = 0
    const total = results.filter(r => r._submitStatus === 'pending').length
    const newPickItems = {}

    for (let i = 0; i < results.length; i++) {
      const row = results[i]
      if (row._submitStatus !== 'pending') continue

      results[i] = { ...results[i], _submitStatus: 'running' }
      setSubmitResults(prev => prev.map((r, ri) => ri === i ? { ...r, _submitStatus: 'running', _submitMessage: '' } : r))

      try {
        const payload = rowToPayload(row, deploymentType, emailId)
        await certificateApi.onboard(payload)
        results[i] = { ...results[i], _submitStatus: 'success', _submitMessage: 'Certificate onboarded successfully' }
      } catch (err) {
        const detail = err?.response?.data?.detail
        if (detail?.code === 'multiple_venafi_matches') {
          results[i] = { ...results[i], _submitStatus: 'venafi_pick_needed', _submitMessage: 'Multiple Venafi certificates found for this common name.' }
          const pickEntry = { commonName: row.common_name, matches: detail.matches, payload: rowToPayload(row, deploymentType, emailId) }
          newPickItems[i] = pickEntry
          // Push to state immediately so the dialog opens even while other rows are still submitting
          setVenafiPickItems(prev => ({ ...prev, [i]: pickEntry }))
        } else {
          const msg = typeof detail === 'string' ? detail : (detail?.message || err?.message || 'Submission failed')
          results[i] = { ...results[i], _submitStatus: 'error', _submitMessage: msg }
        }
      }

      done++
      setSubmitProgress(Math.round((done / total) * 100))
      const finalStatus = results[i]._submitStatus
      const finalMessage = results[i]._submitMessage
      setSubmitResults(prev => prev.map((r, ri) => ri === i ? { ...r, _submitStatus: finalStatus, _submitMessage: finalMessage } : r))
    }

    setSubmitting(false)
    // venafiPickItems already updated incrementally; this ensures any race-condition gaps are filled
    if (Object.keys(newPickItems).length > 0) setVenafiPickItems(prev => ({ ...prev, ...newPickItems }))

    const successCount = results.filter(r => r._submitStatus === 'success').length
    const skippedCount = results.filter(r => r._submitStatus === 'skipped').length
    const failCount    = results.filter(r => r._submitStatus === 'error').length
    const pickCount    = Object.keys(newPickItems).length
    setSnackbar(prev => ({
      open: true,
      key: prev.key + 1,
      message: `Submission complete: ${successCount} succeeded, ${failCount} failed${skippedCount > 0 ? `, ${skippedCount} skipped` : ''}${pickCount > 0 ? `, ${pickCount} need certificate selection` : ''}.`,
      severity: failCount === 0 && pickCount === 0 ? 'success' : 'warning',
    }))
  }

  const handleVenafiPickConfirm = async () => {
    const { rowIdx, selectedIdx } = venafiPickDialog
    const item = venafiPickItems[rowIdx]
    const chosen = item.matches[selectedIdx]

    setVenafiPickDialog({ open: false, rowIdx: null, selectedIdx: 0 })

    setSubmitResults(prev => prev.map((r, i) =>
      i === rowIdx ? { ...r, _submitStatus: 'running', _submitMessage: '' } : r
    ))

    let resolvedStatus = 'success'
    let resolvedMessage = 'Certificate onboarded successfully'
    try {
      await certificateApi.onboard({ ...item.payload, venafi_cert_dn: chosen.certificate_dn, venafi_guid: chosen.guid })
      setSubmitResults(prev => prev.map((r, i) =>
        i === rowIdx ? { ...r, _submitStatus: 'success', _submitMessage: 'Certificate onboarded successfully' } : r
      ))
    } catch (err) {
      resolvedStatus = 'error'
      const detail = err?.response?.data?.detail
      resolvedMessage = typeof detail === 'string' ? detail : (detail?.message || err?.message || 'Submission failed')
      setSubmitResults(prev => prev.map((r, i) =>
        i === rowIdx ? { ...r, _submitStatus: 'error', _submitMessage: resolvedMessage } : r
      ))
    }

    // Remove resolved item from the pending picks map
    setVenafiPickItems(prev => {
      const updated = { ...prev }
      delete updated[rowIdx]
      return updated
    })

    // Recompute snackbar summary now that this pick is resolved.
    // Use the closure's submitResults, overriding rowIdx with the just-resolved status.
    // Other rows are final (loop has finished) so the closure value is accurate for them.
    const remainingPickCount = Object.keys(venafiPickItems).filter(k => Number(k) !== rowIdx).length
    const finalResults = submitResults.map((r, i) =>
      i === rowIdx ? { ...r, _submitStatus: resolvedStatus } : r
    )
    const successCount = finalResults.filter(r => r._submitStatus === 'success').length
    const skippedCount = finalResults.filter(r => r._submitStatus === 'skipped').length
    const failCount    = finalResults.filter(r => r._submitStatus === 'error').length
    setSnackbar(prev => ({
      open: true,
      key: prev.key + 1,
      message: `Submission complete: ${successCount} succeeded, ${failCount} failed${skippedCount > 0 ? `, ${skippedCount} skipped` : ''}${remainingPickCount > 0 ? `, ${remainingPickCount} need certificate selection` : ''}.`,
      severity: failCount === 0 && remainingPickCount === 0 ? 'success' : 'warning',
    }))
  }

  const handleReset = () => {
    setActiveStep(0)
    setDeploymentType('')
    setParsedRows([])
    setParseError('')
    setFileName('')
    setExpandedRows({})
    setSubmitting(false)
    setSubmitResults([])
    setSnackbar(s => ({ ...s, open: false }))
    setSubmitProgress(0)
    setVenafiLookupStatus('idle')
    setVenafiLookupProgress(0)
    setVenafiMatches({})
    setCertSelectionDialog({ open: false, rowId: null })
    setEntryMode('manual')
    setManualRows([])
    manualRowIdRef.current = 0
  }

  // ─── Manual Entry helpers ────────────────────────────────────────────────────

  const createEmptyRow = () => {
    const cols = deploymentType === 'aws' ? AWS_COLUMNS : ONPREM_COLUMNS
    const row = { _id: (manualRowIdRef.current += 1), _autoFilledFields: [] }
    cols.forEach(c => { row[c.key] = '' })
    if (deploymentType === 'aws') {
      row.dependency_present  = 'false'
      row.aws_cert_in_onprem  = 'no'
      row.mutual_auth         = 'no'
      row.layer7              = 'no'
    }
    return row
  }

  const updateManualRow = useCallback((id, field, value) => {
    // When common_name is cleared/changed, lift any block and clear all auto-filled fields
    if (field === 'common_name') {
      setTstBlockedRows(prev => { const next = new Set(prev); next.delete(id); return next })
      setNotOnboardedRows(prev => { const next = new Set(prev); next.delete(id); return next })
      setTstAmbiguousMap(prev => { const next = new Map(prev); next.delete(id); return next })
      setManualRows(prev => prev.map(r => {
        if (r._id !== id) return r
        return {
          ...r,
          account_name:       '',
          account_number:     '',
          application_id:     '',
          product_code:       '',
          environment:        '',
          acm_parameter:      '',
          existing_acm_arn1:  '',
          existing_acm_arn2:  '',
          venafi_policy_base: '',
          _autoFilledFields:  [],
        }
      }))
    }
    // When user manually selects an account, clear TST-ambiguous so all fields show
    if (field === 'account_name' && value) {
      setTstAmbiguousMap(prev => { const next = new Map(prev); next.delete(id); return next })
    }
    setManualRows(prev => prev.map(r => {
      if (r._id !== id) return r
      const updated = { ...r, [field]: value }

      // For onprem: auto-detect SDLC environment from common name (only if not already set)
      if (field === 'common_name' && deploymentType === 'onprem' && !r.environment) {
        const detectedEnv = detectEnvFromCn(value)
        if (detectedEnv) updated.environment = detectedEnv
      }

      // layer7 always mirrors mutual_auth
      if (field === 'mutual_auth') {
        updated.layer7 = value  // 'yes' → 'yes', 'no' → 'no'
      }

      // When environment changes, update the tier part of venafi_policy_base (prod ↔ nonprod)
      // while preserving the CA part (digicert / entrust / private)
      if (field === 'environment' && value) {
        const caMatch = (updated.venafi_policy_base || '').match(/-(digicert|entrust|private)$/)
        const ca = caMatch ? caMatch[1] : updated._detectedCa
        if (ca) {
          const newTier = value.toLowerCase() === 'prd' ? 'prod' : 'nonprod'
          updated.venafi_policy_base = `${newTier}-${ca}`
        }
      }

      // When ssm_parameter is set to no, clear ssm_format and all related fields
      if (field === 'ssm_parameter' && value !== 'yes') {
        updated.ssm_format             = ''
        updated.ssm_cert_path          = ''
        updated.ssm_private_key_path   = ''
        updated.ssm_chain_key_path     = ''
        updated.ssm_cert_password_path = ''
        updated.ssm_cert_content_type  = ''
        updated.ssm_cert_param_type    = ''
        updated.ssm_key_content_type   = ''
        updated.ssm_key_param_type     = ''
        updated.ssm_chain_content_type = ''
        updated.ssm_chain_param_type   = ''
        updated.ssm_password_content_type = ''
        updated.ssm_password_param_type   = ''
      }

      // When sm_parameter is set to no, clear sm_format and related fields
      if (field === 'sm_parameter' && value !== 'yes') {
        updated.sm_format    = ''
        updated.sm_cert_path = ''
      }

      // When ssm_format changes, auto-fill paths + defaults (same as single onboarding)
      if (field === 'ssm_format') {
        const fmt = value
        const fmtLower = fmt.toLowerCase()
        const sdlc = updated.environment ? updated.environment.toLowerCase() : '<env>'
        const cn = updated.common_name || ''
        updated.ssm_cert_path          = cn && fmtLower ? `/venafi/certificates/${sdlc}/${cn}/${fmtLower}/cert` : ''
        updated.ssm_private_key_path   = fmt === 'PEM' && cn ? `/venafi/certificates/${sdlc}/${cn}/${fmtLower}/key` : ''
        updated.ssm_chain_key_path     = fmt === 'PEM' && cn ? `/venafi/certificates/${sdlc}/${cn}/${fmtLower}/chain` : ''
        updated.ssm_cert_password_path = fmt === 'PFX' && cn ? `/venafi/certificates/${sdlc}/${cn}/${fmtLower}/password` : ''
        if (fmt === 'PEM') {
          updated.ssm_cert_content_type     = 'String'
          updated.ssm_cert_param_type       = 'Standard'
          updated.ssm_key_content_type      = 'String'
          updated.ssm_key_param_type        = 'Standard'
          updated.ssm_chain_content_type    = 'String'
          updated.ssm_chain_param_type      = 'Advanced'
          updated.ssm_password_content_type = ''
          updated.ssm_password_param_type   = ''
        } else if (fmt === 'PFX') {
          updated.ssm_cert_content_type     = 'String'
          updated.ssm_cert_param_type       = 'Advanced'
          updated.ssm_key_content_type      = ''
          updated.ssm_key_param_type        = ''
          updated.ssm_chain_content_type    = ''
          updated.ssm_chain_param_type      = ''
          updated.ssm_password_content_type = 'String'
          updated.ssm_password_param_type   = 'Standard'
        }
      }

      // When sm_format changes, auto-fill sm_cert_path
      if (field === 'sm_format') {
        const sdlc = updated.environment ? updated.environment.toLowerCase() : '<env>'
        const cn = updated.common_name || ''
        updated.sm_cert_path = cn ? `/venafi/certificates/${sdlc}/${cn}` : ''
      }

      // When common_name or environment changes, regenerate SSM + SM paths if formats already set
      if (field === 'common_name' || field === 'environment') {
        const sdlc = updated.environment ? updated.environment.toLowerCase() : '<env>'
        const cn = updated.common_name || ''
        if (updated.ssm_format) {
          const fmt = updated.ssm_format
          const fmtLower = fmt.toLowerCase()
          updated.ssm_cert_path          = cn && fmtLower ? `/venafi/certificates/${sdlc}/${cn}/${fmtLower}/cert` : ''
          updated.ssm_private_key_path   = fmt === 'PEM' && cn ? `/venafi/certificates/${sdlc}/${cn}/${fmtLower}/key` : ''
          updated.ssm_chain_key_path     = fmt === 'PEM' && cn ? `/venafi/certificates/${sdlc}/${cn}/${fmtLower}/chain` : ''
          updated.ssm_cert_password_path = fmt === 'PFX' && cn ? `/venafi/certificates/${sdlc}/${cn}/${fmtLower}/password` : ''
        }
        if (updated.sm_format) {
          updated.sm_cert_path = cn ? `/venafi/certificates/${sdlc}/${cn}` : ''
        }
      }

      return updated
    }))
  }, [deploymentType])

  // ── Bulk ACM lookup map: { [domain_name]: { existing_acm_arn1, existing_acm_arn2 } }
  // Fetched once on mount; all per-row lookups are local map reads.
  const acmMapRef = useRef({})
  useEffect(() => {
    inventoryCertificateApi.acmBulk()
      .then(({ data }) => { acmMapRef.current = data || {} })
      .catch(() => { /* non-fatal — fields stay empty */ })
  }, [])

  const deleteManualRow = useCallback((id) => {
    setManualRows(prev => prev.filter(r => r._id !== id))
    setTstBlockedRows(prev => { const next = new Set(prev); next.delete(id); return next })
    setNotOnboardedRows(prev => { const next = new Set(prev); next.delete(id); return next })
  }, [])

  // When common_name loses focus, look up in the pre-fetched ACM map (no API call)
  // and auto-fill existing_acm_arn1/2. Also auto-selects account_name if the AWS
  // account is onboarded and has exactly one environment (unambiguous match).
  // TST accounts cover both QA and UAT — only auto-fill them when the common name
  // itself contains "qa" or "uat", otherwise leave account fields for manual entry.

  // Core auto-fill logic — applies a single ACM inventory entry to a row.
  // Called directly when there is exactly one CA match, or after the user picks
  // a CA from the caSelectDialog when multiple matches exist.
  const applyAcmEntry = useCallback((rowId, commonName, entry) => {
    const toFullArn = (region, accountId, certIdOrArn) => {
      if (!certIdOrArn) return ''
      if (certIdOrArn.startsWith('arn:aws:acm:')) return certIdOrArn
      return accountId ? `arn:aws:acm:${region}:${accountId}:certificate/${certIdOrArn}` : certIdOrArn
    }

    const updates = {
      acm_parameter:     'yes',
      existing_acm_arn1: toFullArn('us-east-1', entry.aws_account_id, entry.existing_acm_arn1),
      existing_acm_arn2: toFullArn('us-west-2', entry.aws_account_id, entry.existing_acm_arn2),
    }

    const cnLower = commonName.toLowerCase()

    if (entry.aws_account_id) {
      const matches = flattenedAccountsRef.current.filter(
        a => a.account_number === entry.aws_account_id
      )

      if (matches.length === 0) {
        const displayName = entry.aws_account_name || entry.aws_account_id
        setSnackbar(prev => ({
          open: true,
          key: prev.key + 1,
          severity: 'error',
          message: `Account "${displayName}" has not been onboarded yet. Please onboard this account before processing its certificates.`,
        }))
        setNotOnboardedRows(prev => new Set(prev).add(rowId))
        setManualRows(prev => prev.map(r =>
          r._id !== rowId ? r : { ...r, ...updates }
        ))
        return
      }

      const inventoryAccountName = (
        entry.aws_account_name || matches[0]?.account_name || ''
      ).toLowerCase()
      const isTst = inventoryAccountName.includes('tst')

      let eligibleMatches = matches
      if (isTst) {
        const onboardedEnvs = matches.map(a => a.product_env?.toLowerCase())
        const missingEnvs = ['qa', 'uat'].filter(e => !onboardedEnvs.includes(e))

        if (missingEnvs.length > 0) {
          const displayName = entry.aws_account_name || entry.aws_account_id || 'TST account'
          setSnackbar(prev => ({
            open: true,
            key: prev.key + 1,
            severity: 'error',
            message: `Account "${displayName}" requires both QA and UAT environments to be onboarded before any certificate on this account can be processed. Missing: ${missingEnvs.map(e => e.toUpperCase()).join(', ')}. Please remove this entry and complete the account onboarding first.`,
          }))
          setTstBlockedRows(prev => new Set(prev).add(rowId))
          eligibleMatches = []
        } else {
          if (cnLower.includes('uat')) {
            eligibleMatches = matches.filter(a => a.product_env?.toLowerCase() === 'uat')
          } else if (cnLower.includes('qa')) {
            eligibleMatches = matches.filter(a => a.product_env?.toLowerCase() === 'qa')
          } else {
            eligibleMatches = []
            // CN gives no environment signal — mark row as TST-ambiguous so all
            // columns except common_name and account_name are hidden until the
            // user updates the CN or manually picks a TST account.
            setTstAmbiguousMap(prev => { const next = new Map(prev); next.set(rowId, matches); return next })
          }
        }
      }

      if (eligibleMatches.length === 1) {
        const m = eligibleMatches[0]
        updates.account_name    = m.account_name
        updates.account_number  = m.account_number
        updates.application_id  = m.application_id || ''
        updates.product_code    = m.product_code   || ''
        updates.environment     = m.product_env    || ''
      }
    }

    if (entry.issuer) {
      const issuerLower = entry.issuer.toLowerCase()
      const env = (updates.environment || '').toLowerCase()
      const isNonProd = ['dev', 'qa', 'uat'].includes(env)
      const isProd    = env === 'prd'
      const isDigiCert = issuerLower.includes('digicert')
      const isEntrust  = issuerLower.includes('broadridge financial solutions')

      if ((isDigiCert || isEntrust) && (isNonProd || isProd)) {
        const tier = isProd ? 'prod' : 'nonprod'
        const ca   = isDigiCert ? 'digicert' : 'private'
        updates.venafi_policy_base = `${tier}-${ca}`
      } else if (isDigiCert || isEntrust) {
        // Environment unknown yet (e.g. TST-ambiguous); store CA so policy base
        // can be computed once the user picks an account/environment.
        updates._detectedCa = isDigiCert ? 'digicert' : 'private'
      }
    }

    const newAutoFilled = Object.keys(updates).filter(k => updates[k] !== '' && updates[k] != null)
    setManualRows(prev => prev.map(r =>
      r._id !== rowId ? r : {
        ...r,
        ...updates,
        _autoFilledFields: [...new Set([...(r._autoFilledFields || []), ...newAutoFilled])],
      }
    ))
  }, [])

  const handleAcmLookup = useCallback((rowId, commonName) => {
    const entries = acmMapRef.current[commonName]  // now an array
    // Clear any previous not-onboarded block for this row before re-evaluating
    setNotOnboardedRows(prev => { const next = new Set(prev); next.delete(rowId); return next })
    // Clear any previous TST-ambiguous state — applyAcmEntry will re-set it if still needed
    setTstAmbiguousMap(prev => { const next = new Map(prev); next.delete(rowId); return next })
    if (!entries || entries.length === 0) return

    // If ALL entries across all accounts have InUse=false, show the ARN selection dialog
    // (only when the cert spans multiple accounts; single-account all-false is applied directly)
    // Skip the dialog entirely if no entry has any ARN at all.
    const allInUseFalse = entries.every(e => !e.in_use)
    const entriesWithArns = entries.filter(e => (e.existing_acm_arn1 || e.existing_acm_arn2) && e.status !== 'EXPIRED')
    if (allInUseFalse && entries.length > 1 && entriesWithArns.length > 0) {
      setArnSelectDialog({ open: true, rowId, commonName, entries: entriesWithArns, selectedIdx: 0 })
      return
    }

    if (entries.length > 1) {
      // Multiple CAs found — ask the user which one to use (exclude EXPIRED)
      const nonExpiredEntries = entries.filter(e => e.status !== 'EXPIRED')
      if (nonExpiredEntries.length === 1) {
        applyAcmEntry(rowId, commonName, nonExpiredEntries[0])
        return
      }
      setCaSelectDialog({ open: true, rowId, commonName, entries: nonExpiredEntries.length > 0 ? nonExpiredEntries : entries, selectedIdx: 0 })
      return
    }

    applyAcmEntry(rowId, commonName, entries[0])
  }, [applyAcmEntry])

  const validateAndProceedManual = () => {
    if (manualRows.length === 0) return

    // Detect duplicate common_names
    const grouped = {}
    manualRows.forEach(row => {
      const cn = (row.common_name || '').trim()
      if (!cn) return
      if (!grouped[cn]) grouped[cn] = []
      grouped[cn].push(row)
    })
    const duplicateGroups = Object.entries(grouped)
      .filter(([, rows]) => rows.length > 1)
      .map(([commonName, rows]) => ({ commonName, rows, selectedId: rows[0]._id }))

    if (duplicateGroups.length > 0) {
      setDuplicateDialog({ open: true, groups: duplicateGroups, source: 'manual', allRows: manualRows })
      return
    }

    proceedWithValidation(manualRows)
  }

  const validateAndProceedFile = () => {
    if (parsedRows.length === 0) return

    // Check for duplicates in the already-parsed rows
    const grouped = {}
    parsedRows.forEach(row => {
      const cn = (row.common_name || '').trim()
      if (!cn) return
      if (!grouped[cn]) grouped[cn] = []
      grouped[cn].push(row)
    })
    const duplicateGroups = Object.entries(grouped)
      .filter(([, r]) => r.length > 1)
      .map(([commonName, r]) => ({ commonName, rows: r, selectedId: r[0]._id }))

    if (duplicateGroups.length > 0) {
      setDuplicateDialog({ open: true, groups: duplicateGroups, source: 'file', allRows: parsedRows })
      return
    }

    setActiveStep(3)
  }

  const proceedWithValidation = (rows) => {
    const validated = rows.map(row => {
      const normalized = normalizeRow(row)
      return {
        ...normalized,
        _id: row._id,
        _validation: validateRow(normalized, deploymentType),
        _submitStatus: 'pending',
        _submitMessage: '',
      }
    })
    setParsedRows(validated)
    setSubmitResults([])
    setActiveStep(3)
  }

  const handleDuplicateResolve = () => {
    const { groups, source, allRows } = duplicateDialog
    const duplicateIds = new Set(groups.flatMap(g => g.rows.map(r => r._id)))
    const keepIds = new Set(groups.map(g => g.selectedId))

    if (source === 'file') {
      const resolvedRows = allRows.filter(r => !duplicateIds.has(r._id) || keepIds.has(r._id))
      setParsedRows(resolvedRows)
      setDuplicateDialog({ open: false, groups: [], source: '', allRows: [] })
      setActiveStep(3)
    } else {
      const resolvedRows = manualRows.filter(r => !duplicateIds.has(r._id) || keepIds.has(r._id))
      setManualRows(resolvedRows)
      setDuplicateDialog({ open: false, groups: [], source: '', allRows: [] })
      proceedWithValidation(resolvedRows)
    }
  }

  const renderManualTable = () => {
    const cols = deploymentType === 'aws' ? AWS_COLUMNS : ONPREM_COLUMNS
    return (
      <Box>
        <TableContainer component={Paper} variant="outlined" sx={{ overflowX: 'auto', borderRadius: 2, maxHeight: 520 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700, bgcolor: 'grey.100', whiteSpace: 'nowrap', minWidth: 36, position: 'sticky', left: 0, zIndex: 3 }}>#</TableCell>
                {cols.map(col => (
                  <TableCell
                    key={col.key}
                    sx={{
                      fontFamily: 'monospace',
                      fontSize: '0.7rem',
                      fontWeight: 700,
                      whiteSpace: 'nowrap',
                      minWidth: DROPDOWN_OPTIONS[col.key] ? 130 : 180,
                      color: col.required ? 'error.main' : 'text.secondary',
                      bgcolor: col.required ? 'error.50' : 'grey.50',
                      py: 1,
                    }}
                  >
                    {col.label}{col.required ? ' *' : ''}
                  </TableCell>
                ))}
                <TableCell sx={{ bgcolor: 'grey.50', minWidth: 44 }} />
              </TableRow>
            </TableHead>
            <TableBody>
              {manualRows.map((row, rowIdx) => (
                <ManualTableRow
                  key={row._id}
                  row={row}
                  rowIdx={rowIdx}
                  cols={cols}
                  updateManualRow={updateManualRow}
                  onDelete={deleteManualRow}
                  allAwsAccounts={deploymentType === 'aws' ? allAwsAccounts : []}
                  flatAccounts={deploymentType === 'aws' ? flattenedAccounts : []}
                  itsmGroupOptions={itsmGroupOptions}
                  onpremProductOptions={onpremProductOptions}
                  onAcmLookup={deploymentType === 'aws' ? handleAcmLookup : null}
                  isBlocked={tstBlockedRows.has(row._id)}
                  isNotOnboarded={notOnboardedRows.has(row._id)}
                  isTstAmbiguous={tstAmbiguousMap.has(row._id)}
                  tstAccounts={tstAmbiguousMap.get(row._id) || []}
                />
              ))}
              {manualRows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={cols.length + 2} sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
                    No rows yet — click <strong>Add Row</strong> to start.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <Box sx={{ mt: 2, display: 'flex', gap: 2, alignItems: 'center' }}>
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={() => setManualRows(prev => [...prev, createEmptyRow()])}
            disabled={tstBlockedRows.size > 0}
            title={tstBlockedRows.size > 0 ? 'Complete TST account onboarding before adding more rows' : ''}
          >
            Add Row
          </Button>
          {manualRows.length > 0 && (
            <Typography variant="caption" color="text.secondary">
              {manualRows.length} row{manualRows.length !== 1 ? 's' : ''} · Required columns marked in red
            </Typography>
          )}
        </Box>
      </Box>
    )
  }

  // ─── Render Steps ────────────────────────────────────────────────────────────

  const renderStep0 = () => (
    <Box>
      <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 1 }}>
        Select Deployment Target
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Choose where these pre-existing certificates will be deployed. All certificates in your upload will share the same deployment type.
      </Typography>
      <Grid container spacing={3}>
        {[
          {
            type: 'aws',
            icon: <CloudIcon sx={{ fontSize: 56 }} />,
            title: 'AWS',
            subtitle: 'Certificate stored in ACM, SSM, or Secrets Manager',
            color: 'primary',
          },
          {
            type: 'onprem',
            icon: <StorageIcon sx={{ fontSize: 56 }} />,
            title: 'On-Premises',
            subtitle: 'Certificate deployed to on-premises infrastructure via Venafi',
            color: 'secondary',
          },
        ].map(({ type, icon, title, subtitle, color }) => (
          <Grid item xs={12} sm={6} key={type}>
            <Paper
              elevation={deploymentType === type ? 6 : 1}
              onClick={() => handleDeploymentSelect(type)}
              sx={{
                p: 4,
                cursor: 'pointer',
                border: deploymentType === type ? '2.5px solid' : '1.5px solid',
                borderColor: deploymentType === type ? `${color}.main` : 'grey.200',
                borderRadius: 3,
                textAlign: 'center',
                transition: 'all 0.2s ease',
                bgcolor: deploymentType === type ? `${color}.50` : 'background.paper',
                '&:hover': {
                  borderColor: `${color}.main`,
                  bgcolor: `${color}.50`,
                  transform: 'translateY(-2px)',
                  boxShadow: 4,
                },
              }}
            >
              <Box sx={{ color: deploymentType === type ? `${color}.main` : 'grey.400', mb: 2 }}>
                {icon}
              </Box>
              <Typography variant="h6" fontWeight={700} gutterBottom>
                {title}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {subtitle}
              </Typography>
              {deploymentType === type && (
                <Chip
                  icon={<CheckCircleIcon />}
                  label="Selected"
                  color={color}
                  size="small"
                  sx={{ mt: 2 }}
                />
              )}
            </Paper>
          </Grid>
        ))}
      </Grid>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 4 }}>
        <Button
          variant="contained"
          size="large"
          disabled={!deploymentType}
          onClick={() => setActiveStep(1)}
          sx={{ px: 4 }}
        >
          Next: Get Template
        </Button>
      </Box>
    </Box>
  )

  const renderStep1 = () => {
    const cols = deploymentType === 'aws' ? AWS_COLUMNS : ONPREM_COLUMNS
    const requiredCols = cols.filter(c => c.required)
    const optionalCols = cols.filter(c => !c.required)

    return (
      <Box>
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 1 }}>
          Required Attributes
        </Typography>
        <Alert severity="info" sx={{ mb: 3, display: 'none' }}>
          Download the template for <strong>{deploymentType === 'aws' ? 'AWS' : 'On-Premises'}</strong> bulk onboarding.
          {deploymentType === 'onprem' ? (
            <> Only <strong>common_name</strong>, <strong>product_code</strong>, and <strong>product_name</strong> are required.
            All other fields (environment, certificate usage, SANs, policy base, etc.) will be <strong>automatically populated from Venafi</strong>.
            You can still provide overrides in the template if needed.</>
          ) : (
            <> Fill in your certificate data, then upload the completed file in the next step.</>
          )}
          {' '}The template includes one sample row to guide you.
        </Alert>

        <Card variant="outlined" sx={{ mb: 3, borderRadius: 2, display: 'none' }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <ArticleIcon color="primary" sx={{ fontSize: 48 }} />
                <Box>
                  <Typography variant="subtitle1" fontWeight={700}>
                    bulk_upload_{deploymentType}_template.csv
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {cols.length} columns · {requiredCols.length} required · 1 sample row
                  </Typography>
                </Box>
              </Box>
              <Button
                variant="contained"
                startIcon={<DownloadIcon />}
                size="large"
                onClick={() => generateXLSXTemplate(deploymentType)}
              >
                Download Template (.xlsx)
              </Button>
            </Box>
          </CardContent>
        </Card>

        <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
          <Button variant="outlined" startIcon={<InfoIcon />} onClick={() => setColumnDialogOpen(true)}>
            View All {cols.length} Column Definitions
          </Button>
        </Box>

        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Card variant="outlined" sx={{ borderRadius: 2, height: '100%' }}>
              <CardContent>
                <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 700, color: 'error.main' }}>
                  Required Columns ({requiredCols.length})
                </Typography>
                <Stack spacing={0.5} sx={{ mt: 1 }}>
                  {requiredCols.map(c => (
                    <Box key={c.key} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                      <Chip label={c.key} size="small" color="error" variant="outlined" sx={{ fontFamily: 'monospace', fontSize: '0.72rem', height: 22 }} />
                    </Box>
                  ))}
                </Stack>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={6}>
            <Card variant="outlined" sx={{ borderRadius: 2, height: '100%' }}>
              <CardContent>
                <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 700, color: 'text.secondary' }}>
                  Optional Columns ({optionalCols.length})
                </Typography>
                <Stack spacing={0.5} sx={{ mt: 1 }}>
                  {optionalCols.map(c => (
                    <Box key={c.key} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                      <Chip label={c.key} size="small" variant="outlined" color="default" sx={{ fontFamily: 'monospace', fontSize: '0.72rem', height: 22, color: 'text.secondary' }} />
                    </Box>
                  ))}
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        <Alert severity="warning" sx={{ mt: 3 }}>
          <Typography variant="body2" gutterBottom fontWeight={600}>Tips before uploading:</Typography>
          <ul style={{ margin: 0, paddingLeft: 20 }}>
            {deploymentType === 'onprem' && (
              <li>Only <strong>common_name, product_code, product_name</strong> are required — all other fields are auto-populated from Venafi</li>
            )}
            <li>Use <strong>pipe characters (|)</strong> to separate multiple SAN entries or dependencies within a single cell</li>
            <li>All certificates in the file are treated as <strong>pre-existing</strong></li>
            <li>Leave optional fields blank; do not remove their columns</li>
            <li>Upload the completed <strong>CSV</strong> file</li>
            <li>Enum fields (dropdowns) are <strong>case-insensitive</strong> — values are normalised automatically on upload</li>
            {deploymentType === 'onprem' && (
              <li>If multiple certificates share the same common name, you will be prompted to select the correct one</li>
            )}
          </ul>
        </Alert>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
          <Button variant="outlined" onClick={() => setActiveStep(0)}>Back</Button>
          <Button variant="contained" size="large" onClick={() => setActiveStep(2)} sx={{ px: 4 }}>
            Next: Upload File
          </Button>
        </Box>

        {/* Column Reference Dialog */}
        <Dialog open={columnDialogOpen} onClose={() => setColumnDialogOpen(false)} maxWidth="md" fullWidth>
          <DialogTitle>
            Column Reference — {deploymentType === 'aws' ? 'AWS' : 'On-Premises'}
            <Typography variant="caption" display="block" color="text.secondary">
              {cols.length} total columns
            </Typography>
          </DialogTitle>
          <DialogContent dividers>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: 'grey.50' }}>
                    <TableCell sx={{ fontWeight: 700 }}>Column</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Required</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Description</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {cols.map(c => (
                    <TableRow key={c.key} hover>
                      <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8rem', whiteSpace: 'nowrap' }}>{c.key}</TableCell>
                      <TableCell>
                        {c.required
                          ? <Chip label="Required" color="error" size="small" variant="outlined" />
                          : <Chip label="Optional" color="default" size="small" variant="outlined" />
                        }
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.82rem', color: 'text.secondary' }}>{c.description}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setColumnDialogOpen(false)}>Close</Button>
          </DialogActions>
        </Dialog>
      </Box>
    )
  }

  const renderStep2 = () => (
    <Box>
      <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 1 }}>
        Add Certificate Data
      </Typography>

      {/* Manual entry table */}
      {renderManualTable()}

      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
        <Button variant="outlined" onClick={() => setActiveStep(1)}>Back</Button>
        <Button
          variant="contained"
          size="large"
          disabled={manualRows.length === 0 || notOnboardedRows.size > 0 || tstBlockedRows.size > 0}
          onClick={validateAndProceedManual}
          sx={{ px: 4 }}
        >
          Validate & Review ({manualRows.length} row{manualRows.length !== 1 ? 's' : ''})
        </Button>
      </Box>
    </Box>
  )

  // ─── Venafi Lookup Step (On-Prem only, step index 3) ──────────────────────
  const renderStepVenafiLookup = () => {
    const numTotal     = parsedRows.length
    const numSingle    = Object.values(venafiMatches).filter(m => m.status === 'single' && m.details).length
    const numMultiple  = Object.values(venafiMatches).filter(m => m.status === 'multiple' || m.status === 'fetching-details').length
    const numResolved  = Object.values(venafiMatches).filter(m => (m.status === 'multiple' || m.status === 'fetching-details') && m.selectedMatch && m.details).length
    const numError     = Object.values(venafiMatches).filter(m => m.status === 'error' || m.status === 'not-found' || m.status === 'already-onboarded').length

    // parseDN utility
    const parseDN = (dn) => {
      const parts = dn.replace(/\\\\/g, '\\').split('\\').filter(Boolean)
      const cn = parts[parts.length - 1] || dn
      const isProd = !dn.toLowerCase().includes('non-production') && dn.toLowerCase().includes('production')
      const folder = parts.slice(0, -1).join(' \u203A ')
      // Detect CA from path
      let ca = ''
      if (dn.toLowerCase().includes('digicert')) ca = 'DigiCert'
      else if (dn.toLowerCase().includes('entrust')) ca = 'Entrust'
      return { cn, isProd, folder, ca }
    }

    return (
      <Box>
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 1 }}>
          <SearchIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
          Venafi Certificate Lookup
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Each certificate will be looked up in Venafi by its common name. Fields like environment, certificate usage,
          SANs, and policy base will be automatically populated from Venafi data.
        </Typography>

        {venafiLookupStatus === 'idle' && (
          <>
            <Alert severity="info" sx={{ mb: 3 }}>
              Click <strong>Start Venafi Lookup</strong> to search for all {numTotal} certificate{numTotal !== 1 ? 's' : ''} in Venafi.
              If multiple certificates share the same common name, you&apos;ll be asked to choose.
            </Alert>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
              <Button variant="outlined" onClick={() => setActiveStep(2)}>Back</Button>
              <Button variant="contained" size="large" startIcon={<SearchIcon />} onClick={runVenafiLookup} sx={{ px: 4 }}>
                Start Venafi Lookup ({numTotal} certificates)
              </Button>
            </Box>
          </>
        )}

        {venafiLookupStatus === 'running' && (
          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography variant="body2" color="text.secondary">
                Searching Venafi for certificates...
              </Typography>
              <Typography variant="body2" fontWeight={600}>{venafiLookupProgress}%</Typography>
            </Box>
            <LinearProgress variant="determinate" value={venafiLookupProgress} sx={{ height: 8, borderRadius: 4 }} />
          </Box>
        )}

        {(venafiLookupStatus === 'running' || venafiLookupStatus === 'done') && (
          <>
            {/* Summary chips */}
            <Box sx={{ display: 'flex', gap: 1.5, mb: 3, flexWrap: 'wrap' }}>
              <Chip icon={<CheckCircleIcon />} label={`${numSingle} Auto-matched`} color="success" size="small" />
              {numMultiple > 0 && (
                <Chip
                  icon={<WarningIcon />}
                  label={`${numMultiple} Need selection (${numResolved} resolved)`}
                  color={numResolved === numMultiple ? 'success' : 'warning'}
                  size="small"
                />
              )}
              {numError > 0 && <Chip icon={<ErrorIcon />} label={`${numError} Failed`} color="error" size="small" />}
            </Box>

            {/* Row-by-row results */}
            <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2, mb: 3, maxHeight: 500, overflow: 'auto' }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700, bgcolor: 'grey.100', width: 40 }}>#</TableCell>
                    <TableCell sx={{ fontWeight: 700, bgcolor: 'grey.100' }}>Common Name</TableCell>
                    <TableCell sx={{ fontWeight: 700, bgcolor: 'grey.100' }}>Product</TableCell>
                    <TableCell sx={{ fontWeight: 700, bgcolor: 'grey.100', width: 160 }}>Venafi Status</TableCell>
                    <TableCell sx={{ fontWeight: 700, bgcolor: 'grey.100' }}>Selected Certificate</TableCell>
                    <TableCell sx={{ fontWeight: 700, bgcolor: 'grey.100', width: 100 }}>Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {parsedRows.map((row, idx) => {
                    const lookup = venafiMatches[row._id]
                    const isLoading = !lookup
                    const isSingle = lookup?.status === 'single' && lookup?.details
                    const isMultiple = lookup?.status === 'multiple' || lookup?.status === 'fetching-details'
                    const hasSelection = isMultiple && lookup?.selectedMatch && lookup?.details
                    const isError = lookup?.status === 'error' || lookup?.status === 'not-found' || lookup?.status === 'already-onboarded'

                    let statusChip
                    if (isLoading) statusChip = <Chip label="Waiting..." size="small" color="default" />
                    else if (isSingle) statusChip = <Chip icon={<CheckCircleIcon />} label="Auto-matched" size="small" color="success" />
                    else if (isMultiple && hasSelection) statusChip = <Chip icon={<CheckCircleIcon />} label="Selected" size="small" color="success" />
                    else if (isMultiple) statusChip = <Chip icon={<WarningIcon />} label={`${lookup.matches.length} matches`} size="small" color="warning" />
                    else if (lookup?.status === 'already-onboarded') statusChip = <Chip icon={<ErrorIcon />} label="Already Onboarded" size="small" color="error" variant="outlined" />
                    else if (isError) statusChip = <Chip icon={<ErrorIcon />} label="Not Found" size="small" color="error" />
                    else statusChip = <Chip label="..." size="small" />

                    const selectedDn = lookup?.selectedMatch?.certificate_dn || lookup?.selectedMatch?.policy_dn
                    const selectedParsed = selectedDn ? parseDN(selectedDn) : null

                    return (
                      <TableRow
                        key={row._id}
                        sx={{
                          bgcolor: isError ? 'error.50' : (isMultiple && !hasSelection) ? 'warning.50' : 'inherit',
                        }}
                      >
                        <TableCell sx={{ fontSize: '0.78rem', color: 'text.secondary' }}>{idx + 1}</TableCell>
                        <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.82rem' }}>{row.common_name}</TableCell>
                        <TableCell sx={{ fontSize: '0.82rem' }}>{row.product_code}</TableCell>
                        <TableCell>{statusChip}</TableCell>
                        <TableCell sx={{ fontSize: '0.78rem' }}>
                          {(isSingle || hasSelection) && selectedParsed ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <LockIcon sx={{ fontSize: 14, color: 'success.main' }} />
                              <Box>
                                <Typography variant="caption" sx={{ fontFamily: 'monospace', display: 'block' }}>
                                  {selectedParsed.cn}
                                </Typography>
                                {selectedParsed.ca && (
                                  <Chip label={selectedParsed.ca} size="small" sx={{ height: 16, fontSize: '0.6rem' }} />
                                )}
                                {selectedParsed.isProd !== undefined && (
                                  <Chip
                                    label={selectedParsed.isProd ? 'PRD' : 'NON-PRD'}
                                    size="small"
                                    sx={{ height: 16, fontSize: '0.6rem', ml: 0.5,
                                      bgcolor: selectedParsed.isProd ? 'error.dark' : 'info.dark', color: '#fff' }}
                                  />
                                )}
                              </Box>
                            </Box>
                          ) : isError ? (
                            <Typography variant="caption" color="error.main">{lookup?.error}</Typography>
                          ) : (
                            <Typography variant="caption" color="text.secondary">—</Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          {isMultiple && !hasSelection && (
                            <Button
                              size="small"
                              variant="contained"
                              color="warning"
                              onClick={() => setCertSelectionDialog({ open: true, rowId: row._id })}
                            >
                              Choose
                            </Button>
                          )}
                          {isMultiple && hasSelection && (
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => setCertSelectionDialog({ open: true, rowId: row._id })}
                            >
                              Change
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    )
                  })}
                </TableBody>
              </Table>
            </TableContainer>

            {venafiLookupStatus === 'done' && (
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3, flexWrap: 'wrap', gap: 2 }}>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Button variant="outlined" onClick={() => { setVenafiLookupStatus('idle'); setVenafiMatches({}); setActiveStep(2) }}>
                    Re-upload File
                  </Button>
                  <Button variant="outlined" startIcon={<RefreshIcon />} onClick={runVenafiLookup}>
                    Re-run Lookup
                  </Button>
                </Box>
                <Button
                  variant="contained"
                  size="large"
                  disabled={!allMultiMatchesResolved}
                  onClick={applyVenafiEnrichmentAndProceed}
                  sx={{ px: 4 }}
                >
                  {allMultiMatchesResolved
                    ? `Continue to Review (${numSingle + numResolved} enriched)`
                    : `Resolve ${numMultiple - numResolved} remaining selection${numMultiple - numResolved !== 1 ? 's' : ''}`
                  }
                </Button>
              </Box>
            )}
          </>
        )}

        {/* ─── Certificate Selection Dialog (for multi-match rows) ───────────── */}
        <Dialog
          open={certSelectionDialog.open}
          onClose={() => setCertSelectionDialog({ open: false, rowId: null })}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            Select Certificate for Row {certSelectionDialog.rowId !== null
              ? parsedRows.findIndex(r => r._id === certSelectionDialog.rowId) + 1
              : ''}
            <Typography variant="caption" display="block" color="text.secondary">
              {certSelectionDialog.rowId !== null && parsedRows.find(r => r._id === certSelectionDialog.rowId)?.common_name}
              {' — '}
              {venafiMatches[certSelectionDialog.rowId]?.matches?.length || 0} certificates found in Venafi
            </Typography>
          </DialogTitle>
          <DialogContent dividers>
            {certSelectionDialog.rowId !== null && venafiMatches[certSelectionDialog.rowId]?.matches?.map((match, idx) => {
              const parsed = parseDN(match.certificate_dn || match.policy_dn || '')
              const isSelected = venafiMatches[certSelectionDialog.rowId]?.selectedMatch?.guid === match.guid
                || venafiMatches[certSelectionDialog.rowId]?.selectedMatch?.certificate_dn === match.certificate_dn
              const isFetching = isSelected && venafiMatches[certSelectionDialog.rowId]?.status === 'fetching-details'

              return (
                <Paper
                  key={`${match.guid || match.certificate_dn}-${idx}`}
                  elevation={isSelected ? 4 : 1}
                  sx={{
                    mb: 1.5,
                    border: '2px solid',
                    borderColor: isSelected ? 'success.main' : 'grey.200',
                    borderRadius: 2,
                    overflow: 'hidden',
                    transition: 'all 0.2s ease',
                    '&:hover': { borderColor: isSelected ? 'success.main' : 'primary.main', boxShadow: 3 },
                  }}
                >
                  {/* Header */}
                  <Box
                    sx={{
                      px: 2, py: 1.5,
                      background: isSelected
                        ? 'linear-gradient(90deg, #1b5e20 0%, #2e7d32 100%)'
                        : parsed.isProd
                          ? 'linear-gradient(90deg, #1a2c50 0%, #243858 100%)'
                          : 'linear-gradient(90deg, #1a3050 0%, #1f3a60 100%)',
                      display: 'flex', alignItems: 'center', gap: 1.5,
                    }}
                  >
                    <LockIcon sx={{ color: isSelected ? '#a5d6a7' : '#90caf9', fontSize: 18, flexShrink: 0 }} />
                    <Typography
                      sx={{
                        color: '#fff', fontWeight: 600, fontSize: '0.85rem', flex: 1,
                        fontFamily: '"Roboto Mono","Courier New",monospace',
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                      }}
                    >
                      {parsed.cn}
                    </Typography>
                    <Chip
                      label={parsed.isProd ? 'PRD' : 'NON-PRD'}
                      size="small"
                      sx={{ height: 20, fontSize: '0.6rem', fontWeight: 700,
                        bgcolor: parsed.isProd ? 'error.dark' : 'info.dark', color: '#fff', flexShrink: 0 }}
                    />
                    {parsed.ca && (
                      <Chip label={parsed.ca} size="small"
                        sx={{ height: 20, fontSize: '0.6rem', fontWeight: 700, bgcolor: 'rgba(255,255,255,0.2)', color: '#fff' }} />
                    )}
                    {isSelected && <CheckCircleIcon sx={{ color: '#a5d6a7', fontSize: 18, flexShrink: 0 }} />}
                  </Box>

                  {/* Body */}
                  <Box sx={{ px: 2, py: 1.5 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ wordBreak: 'break-all' }}>
                      <strong>DN:</strong> {match.certificate_dn || match.policy_dn}
                    </Typography>
                    {match.guid && (
                      <Typography variant="caption" color="text.secondary" display="block">
                        <strong>GUID:</strong> {match.guid}
                      </Typography>
                    )}
                  </Box>

                  {/* Footer */}
                  <Box sx={{ px: 2, py: 1, bgcolor: 'grey.50', borderTop: '1px solid', borderColor: 'grey.100', display: 'flex', justifyContent: 'flex-end' }}>
                    <Button
                      variant={isSelected ? 'contained' : 'outlined'}
                      color={isSelected ? 'success' : 'primary'}
                      size="small"
                      disabled={isFetching}
                      onClick={() => !isSelected && handleSelectVenafiCert(certSelectionDialog.rowId, match)}
                      startIcon={isFetching ? <CircularProgress size={14} color="inherit" /> : null}
                      sx={{ minWidth: 120 }}
                    >
                      {isFetching ? 'Fetching details…' : isSelected ? 'Selected ✓' : 'Select'}
                    </Button>
                  </Box>
                </Paper>
              )
            })}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setCertSelectionDialog({ open: false, rowId: null })}>Close</Button>
          </DialogActions>
        </Dialog>
      </Box>
    )
  }

  const renderStep3 = () => {
    const cols = deploymentType === 'aws' ? AWS_COLUMNS : ONPREM_COLUMNS
    const visibleCols = cols.slice(0, deploymentType === 'aws' ? 8 : 6) // Show first N cols in table

    return (
      <Box>
        {/* Summary Bar */}
        <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap', alignItems: 'center' }}>
          <Typography variant="h6" sx={{ fontWeight: 600, flexGrow: 1 }}>
            Review & Validate
          </Typography>
          <Chip icon={<CheckCircleIcon />} label={`${validRows.length} Valid`} color="success" />
          {warningRows.length > 0 && <Chip icon={<WarningIcon />} label={`${warningRows.length} Warnings`} color="warning" />}
          {invalidRows.length > 0 && <Chip icon={<ErrorIcon />} label={`${invalidRows.length} Errors`} color="error" />}
          <Chip label={`${parsedRows.length} Total`} color="default" />
        </Box>

        {invalidRows.length > 0 && (
          <Alert severity="error" sx={{ mb: 2 }}>
            <strong>{invalidRows.length} row{invalidRows.length > 1 ? 's have' : ' has'} validation errors</strong> and will be skipped during submission.
            Expand each row to see the specific errors. Fix them in your CSV and re-upload, or remove the rows.
          </Alert>
        )}

        {invalidRows.length === 0 && (
          <Alert severity="success" sx={{ mb: 2 }}>
            All {validRows.length} rows passed validation. Ready to submit.
          </Alert>
        )}

        {deploymentType === 'onprem' && invalidRows.length === 0 && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Fields marked with a <Chip label="Venafi" size="small" color="info" variant="outlined" sx={{ mx: 0.5, height: 18, fontSize: '0.65rem' }} /> badge
            were auto-populated from Venafi certificate data. Expand rows to see all enriched values.
          </Alert>
        )}

        <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2, mb: 3, maxHeight: 500, overflow: 'auto' }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700, bgcolor: 'grey.100', width: 40 }}>#</TableCell>
                <TableCell sx={{ fontWeight: 700, bgcolor: 'grey.100', width: 100 }}>Status</TableCell>
                {visibleCols.map(c => (
                  <TableCell key={c.key} sx={{ fontWeight: 700, bgcolor: 'grey.100', fontFamily: 'monospace', fontSize: '0.78rem', whiteSpace: 'nowrap' }}>
                    {c.key}
                  </TableCell>
                ))}
                <TableCell sx={{ fontWeight: 700, bgcolor: 'grey.100', width: 80 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {parsedRows.map((row, idx) => (
                <React.Fragment key={row._id}>
                  <TableRow
                    hover
                    sx={{
                      bgcolor: row._validation?.errors.length > 0
                        ? 'error.50'
                        : row._validation?.warnings.length > 0
                          ? 'warning.50'
                          : 'inherit',
                    }}
                  >
                    <TableCell sx={{ color: 'text.secondary', fontSize: '0.78rem' }}>{idx + 1}</TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <StatusChip row={row} />
                        <IconButton
                          size="small"
                          onClick={() => toggleRowExpand(row._id)}
                          sx={{ ml: 0.5 }}
                        >
                          {expandedRows[row._id] ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                        </IconButton>
                      </Box>
                    </TableCell>
                    {visibleCols.map(c => (
                      <TableCell
                        key={c.key}
                        sx={{
                          fontFamily: 'monospace',
                          fontSize: '0.78rem',
                          maxWidth: 160,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        <Tooltip title={row[c.key] || '—'} arrow>
                          <span>{row[c.key] || <span style={{ color: '#aaa' }}>—</span>}</span>
                        </Tooltip>
                      </TableCell>
                    ))}
                    <TableCell>
                      <Tooltip title="Remove row">
                        <IconButton size="small" color="error" onClick={() => removeRow(row._id)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>

                  {/* Expanded details row */}
                  <TableRow>
                    <TableCell colSpan={visibleCols.length + 3} sx={{ py: 0, borderBottom: expandedRows[row._id] ? undefined : 'none' }}>
                      <Collapse in={!!expandedRows[row._id]} timeout="auto" unmountOnExit>
                        <Box sx={{ py: 2, px: 3, bgcolor: 'grey.50', borderRadius: 1, mx: 1, my: 1 }}>
                          <Grid container spacing={2}>
                            {/* Errors */}
                            {row._validation?.errors.length > 0 && (
                              <Grid item xs={12} md={6}>
                                <Typography variant="caption" color="error.main" fontWeight={700} gutterBottom display="block">
                                  Errors ({row._validation.errors.length})
                                </Typography>
                                <List dense disablePadding>
                                  {row._validation.errors.map((e, i) => (
                                    <ListItem key={i} disablePadding>
                                      <ErrorIcon color="error" sx={{ fontSize: 14, mr: 1 }} />
                                      <ListItemText primary={e} primaryTypographyProps={{ variant: 'caption', color: 'error.main' }} />
                                    </ListItem>
                                  ))}
                                </List>
                              </Grid>
                            )}
                            {/* Warnings */}
                            {row._validation?.warnings.length > 0 && (
                              <Grid item xs={12} md={6}>
                                <Typography variant="caption" color="warning.dark" fontWeight={700} gutterBottom display="block">
                                  Warnings ({row._validation.warnings.length})
                                </Typography>
                                <List dense disablePadding>
                                  {row._validation.warnings.map((w, i) => (
                                    <ListItem key={i} disablePadding>
                                      <WarningIcon color="warning" sx={{ fontSize: 14, mr: 1 }} />
                                      <ListItemText primary={w} primaryTypographyProps={{ variant: 'caption', color: 'warning.dark' }} />
                                    </ListItem>
                                  ))}
                                </List>
                              </Grid>
                            )}
                            {/* All field values */}
                            <Grid item xs={12}>
                              <Divider sx={{ my: 1 }} />
                              <Typography variant="caption" color="text.secondary" fontWeight={700} display="block" gutterBottom>
                                All Field Values
                              </Typography>
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                {(deploymentType === 'aws' ? AWS_COLUMNS : ONPREM_COLUMNS).map(c => (
                                  row[c.key] ? (
                                    <Chip
                                      key={c.key}
                                      label={`${c.key}: ${row[c.key]}`}
                                      size="small"
                                      variant="outlined"
                                      sx={{ fontFamily: 'monospace', fontSize: '0.7rem', height: 20 }}
                                    />
                                  ) : null
                                ))}
                              </Box>
                            </Grid>
                          </Grid>
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4, flexWrap: 'wrap', gap: 2 }}>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button variant="outlined" onClick={() => setActiveStep(deploymentType === 'onprem' ? 3 : 2)}>Back</Button>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={() => { setParsedRows([]); setFileName(''); setVenafiMatches({}); setVenafiLookupStatus('idle'); setActiveStep(2) }}
            >
              Re-upload File
            </Button>
          </Box>
          <Button
            variant="contained"
            color="success"
            size="large"
            disabled={validRows.length === 0}
            startIcon={<SendIcon />}
            onClick={handleSubmitAll}
            sx={{ px: 4 }}
          >
            Submit {validRows.length} Certificate{validRows.length !== 1 ? 's' : ''}
            {invalidRows.length > 0 && ` (skip ${invalidRows.length} invalid)`}
          </Button>
        </Box>
      </Box>
    )
  }

  const renderStep4 = () => {
    const successCount = submitResults.filter(r => r._submitStatus === 'success').length
    const failCount    = submitResults.filter(r => r._submitStatus === 'error').length
    const pendingCount = submitResults.filter(r => r._submitStatus === 'pending' || r._submitStatus === 'running').length
    const total        = validRows.length

    return (
      <Box>
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 1 }}>
          Bulk Submission Progress
        </Typography>

        {/* Progress Bar */}
        {submitting && (
          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography variant="body2" color="text.secondary">
                Submitting certificates...
              </Typography>
              <Typography variant="body2" fontWeight={600}>
                {submitProgress}%
              </Typography>
            </Box>
            <LinearProgress variant="determinate" value={submitProgress} sx={{ height: 8, borderRadius: 4 }} />
          </Box>
        )}

        {!submitting && (
          <Alert
            severity={failCount === 0 ? 'success' : failCount === total ? 'error' : 'warning'}
            sx={{ mb: 3 }}
            action={
              <Button size="small" onClick={handleReset} startIcon={<RefreshIcon />}>
                Start New Upload
              </Button>
            }
          >
            <Typography fontWeight={700}>
              {failCount === 0
                ? `All ${successCount} certificates onboarded successfully!`
                : `${successCount} succeeded, ${failCount} failed.`
              }
            </Typography>
          </Alert>
        )}

        {/* Summary chips */}
        <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
          {successCount > 0 && <Chip icon={<CheckCircleIcon />} label={`${successCount} Succeeded`} color="success" />}
          {failCount > 0 && <Chip icon={<ErrorIcon />} label={`${failCount} Failed`} color="error" />}
          {pendingCount > 0 && <Chip label={`${pendingCount} Pending`} color="default" />}
          {invalidRows.length > 0 && <Chip label={`${invalidRows.length} Skipped (invalid)`} color="warning" variant="outlined" />}
        </Box>

        {/* Results Table */}
        <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2, maxHeight: 450, overflow: 'auto' }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700, bgcolor: 'grey.100', width: 40 }}>#</TableCell>
                <TableCell sx={{ fontWeight: 700, bgcolor: 'grey.100' }}>Common Name</TableCell>
                <TableCell sx={{ fontWeight: 700, bgcolor: 'grey.100' }}>Product</TableCell>
                <TableCell sx={{ fontWeight: 700, bgcolor: 'grey.100' }}>Environment</TableCell>
                <TableCell sx={{ fontWeight: 700, bgcolor: 'grey.100', width: 140 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 700, bgcolor: 'grey.100' }}>Message</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {submitResults.map((row, idx) => (
                <TableRow
                  key={row._id}
                  sx={{
                    bgcolor: row._submitStatus === 'success'
                      ? 'success.50'
                      : row._submitStatus === 'error'
                        ? 'error.50'
                        : 'inherit'
                  }}
                >
                  <TableCell sx={{ color: 'text.secondary', fontSize: '0.78rem' }}>{idx + 1}</TableCell>
                  <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>{row.common_name}</TableCell>
                  <TableCell sx={{ fontSize: '0.8rem' }}>{row.product_code}</TableCell>
                  <TableCell>
                    {row.environment && (
                      <Chip
                        label={(row.environment || '').toUpperCase()}
                        size="small"
                        color={row.environment === 'prd' ? 'error' : 'default'}
                        variant="outlined"
                      />
                    )}
                  </TableCell>
                  <TableCell>
                    <SubmitStatusChip status={row._submitStatus} />
                    {row._submitStatus === 'skipped' && (
                      <Chip label="Skipped" color="warning" size="small" variant="outlined" />
                    )}
                  </TableCell>
                  <TableCell sx={{ fontSize: '0.78rem', color: row._submitStatus === 'error' ? 'error.main' : 'text.secondary' }}>
                    {row._submitStatus === 'venafi_pick_needed' ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                        <Typography variant="caption" color="warning.main">
                          Multiple Venafi certificates found.
                        </Typography>
                        <Button
                          size="small"
                          variant="outlined"
                          color="warning"
                          onClick={() => setVenafiPickDialog({ open: true, rowIdx: idx, selectedIdx: 0 })}
                        >
                          Select Certificate
                        </Button>
                      </Box>
                    ) : (
                      row._submitMessage || '—'
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {!submitting && failCount > 0 && (
          <Alert severity="info" sx={{ mt: 2 }}>
            <Typography variant="body2">
              To retry failed rows, download the results and fix the errors, then re-upload only the failed rows.
            </Typography>
          </Alert>
        )}
      </Box>
    )
  }

  const stepContent = deploymentType === 'onprem'
    ? [renderStep0, renderStep1, renderStep2, renderStepVenafiLookup, renderStep3, renderStep4]
    : [renderStep0, renderStep1, renderStep2, renderStep3, renderStep4]

  return (
    <Box sx={{ maxWidth: 1100, mx: 'auto' }}>
      {/* Page Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <Badge
          badgeContent={parsedRows.length > 0 ? parsedRows.length : undefined}
          color="primary"
          max={999}
        >
          <SecurityIcon sx={{ fontSize: 40, color: 'primary.main' }} />
        </Badge>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 600, color: 'primary.main' }}>
            Bulk Certificate Onboarding
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Pre-existing certificates only — onboard multiple certificates at once via CSV upload
          </Typography>
        </Box>
      </Box>

      {/* Stepper */}
      <Paper elevation={0} variant="outlined" sx={{ p: 3, mb: 3, borderRadius: 2 }}>
        <Stepper activeStep={activeStep} alternativeLabel>
          {STEPS.map((label, index) => (
            <Step key={label} completed={index < activeStep}>
              <StepLabel
                sx={{
                  '& .MuiStepLabel-label': {
                    fontSize: '0.82rem',
                    fontWeight: index === activeStep ? 700 : 400,
                  }
                }}
              >
                {label}
              </StepLabel>
            </Step>
          ))}
        </Stepper>
      </Paper>

      {/* Step Content */}
      <Paper elevation={2} sx={{ p: 4, borderRadius: 2 }}>
        {stepContent[activeStep]?.()}
      </Paper>

      {/* Snackbar */}
      <Snackbar
        key={snackbar.key}
        open={snackbar.open}
        autoHideDuration={snackbar.severity === 'error' ? null : 7000}
        onClose={snackbar.severity === 'error' ? undefined : () => setSnackbar(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar(s => ({ ...s, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Venafi Multi-Match Dialog — opened via "Select Certificate" button on each row */}
      {venafiPickDialog.open && venafiPickItems[venafiPickDialog.rowIdx] && (() => {
        const item = venafiPickItems[venafiPickDialog.rowIdx]
        const policyBaseLabels = {
          'nonprod-digicert': 'Non-Production · DigiCert',
          'nonprod-private':  'Non-Production · Private',
          'prod-digicert':    'Production · DigiCert',
          'prod-private':     'Production · Private',
        }
        const rowPolicyLabel = policyBaseLabels[item.payload?.venafi_policy_base] || item.payload?.venafi_policy_base || '—'

        // Derive venafi_policy_base key from a certificate DN so we can flag matches
        const derivePolicyBase = (dn) => {
          const isNonProd = dn.includes('Non-Production')
          const isProd    = !isNonProd && dn.includes('Production')
          const isDigiCert = dn.includes('DigiCert')
          const isEntrust  = dn.includes('Entrust')
          if (isNonProd && isDigiCert) return 'nonprod-digicert'
          if (isNonProd && isEntrust)  return 'nonprod-private'
          if (isProd    && isDigiCert) return 'prod-digicert'
          if (isProd    && isEntrust)  return 'prod-private'
          return null
        }

        return (
          <Dialog open onClose={() => setVenafiPickDialog({ open: false, rowIdx: null, selectedIdx: 0 })} maxWidth="sm" fullWidth>
            <DialogTitle>
              Select Venafi Certificate
              <Typography variant="caption" display="block" color="text.secondary" sx={{ wordBreak: 'break-all' }}>
                {item.commonName}
              </Typography>
            </DialogTitle>
            <DialogContent dividers>
              <Box sx={{ mb: 2, p: 1.5, bgcolor: 'action.hover', borderRadius: 1 }}>
                <Typography variant="caption" color="text.secondary">Selected policy in upload row</Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>{rowPolicyLabel}</Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                Multiple certificates exist in Venafi for this common name. Select which one to onboard:
              </Typography>
              <RadioGroup
                value={venafiPickDialog.selectedIdx}
                onChange={e => setVenafiPickDialog(prev => ({ ...prev, selectedIdx: Number(e.target.value) }))}
              >
                {item.matches.map((match, idx) => {
                  const dn = match.certificate_dn || ''
                  const caLabel = dn.includes('DigiCert') ? 'DigiCert'
                                : dn.includes('Entrust')  ? 'Entrust'
                                : dn.split('\\').slice(-2, -1)[0] || 'Unknown CA'
                  const envLabel = dn.includes('Non-Production') ? 'Non-Production'
                                 : dn.includes('Production')     ? 'Production'
                                 : ''
                  const matchesPolicyBase = derivePolicyBase(dn) === item.payload?.venafi_policy_base
                  const isSelected = venafiPickDialog.selectedIdx === idx
                  return (
                    <FormControlLabel
                      key={idx}
                      value={idx}
                      control={<Radio size="small" />}
                      label={
                        <Box sx={{ py: 0.5 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>{envLabel} · {caLabel}</Typography>
                            {matchesPolicyBase && (
                              <Chip label="Matches your selection" size="small" color="success" variant="outlined" sx={{ height: 18, fontSize: '0.68rem' }} />
                            )}
                          </Box>
                          <Typography variant="caption" color="text.secondary" sx={{ wordBreak: 'break-all' }}>{dn}</Typography>
                        </Box>
                      }
                      sx={{ alignItems: 'flex-start', mb: 0.5, border: '1px solid', borderColor: isSelected ? 'primary.main' : 'divider', borderRadius: 1, px: 1, mx: 0 }}
                    />
                  )
                })}
              </RadioGroup>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setVenafiPickDialog({ open: false, rowIdx: null, selectedIdx: 0 })}>Cancel</Button>
              <Button variant="contained" onClick={handleVenafiPickConfirm}>
                Use This Certificate
              </Button>
            </DialogActions>
          </Dialog>
        )
      })()}

      {/* CA Selection Dialog — shown when a common name exists under multiple CAs */}
      <Dialog
        open={caSelectDialog.open}
        onClose={() => setCaSelectDialog({ open: false, rowId: null, commonName: '', entries: [], selectedIdx: 0 })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Multiple Certificates Found
          <Typography variant="caption" display="block" color="text.secondary" sx={{ wordBreak: 'break-all' }}>
            {caSelectDialog.commonName}
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            This certificate exists under multiple Certificate Authorities. Select which one to use:
          </Typography>
          <RadioGroup
            value={caSelectDialog.selectedIdx}
            onChange={e => setCaSelectDialog(prev => ({ ...prev, selectedIdx: Number(e.target.value) }))}
          >
            {caSelectDialog.entries.map((entry, idx) => {
              const issuerLower = (entry.issuer || '').toLowerCase()
              const caLabel = issuerLower.includes('digicert') ? 'DigiCert'
                            : issuerLower.includes('broadridge financial solutions') ? 'Entrust'
                            : entry.issuer || 'Unknown CA'
              const isSelected = caSelectDialog.selectedIdx === idx
              return (
                <FormControlLabel
                  key={idx}
                  value={idx}
                  control={<Radio size="small" />}
                  label={
                    <Box sx={{ py: 0.5 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>{caLabel}</Typography>
                      {(entry.aws_account_name || entry.aws_account_id) && (
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ fontWeight: 500 }}>
                          Account: {entry.aws_account_name || entry.aws_account_id}
                          {entry.aws_account_name && entry.aws_account_id && ` (${entry.aws_account_id})`}
                        </Typography>
                      )}
                      {entry.issuer && (
                        <Typography variant="caption" color="text.secondary" display="block">
                          {entry.issuer}
                          {entry.status && (
                            <Chip
                              label={entry.status}
                              size="small"
                              color={entry.status === 'ISSUED' ? 'success' : entry.status === 'EXPIRED' ? 'error' : 'default'}
                              sx={{ ml: 1, height: 16, fontSize: '0.65rem' }}
                            />
                          )}
                          <Chip
                            label={entry.in_use ? 'InUse' : 'Not InUse'}
                            size="small"
                            color={entry.in_use ? 'success' : 'default'}
                            variant={entry.in_use ? 'filled' : 'outlined'}
                            sx={{ ml: 0.5, height: 16, fontSize: '0.65rem' }}
                          />
                        </Typography>
                      )}
                      {entry.existing_acm_arn1 && (
                        <Typography variant="caption" color="text.secondary" display="block">
                          us-east-1: {entry.existing_acm_arn1}
                        </Typography>
                      )}
                      {entry.existing_acm_arn2 && (
                        <Typography variant="caption" color="text.secondary" display="block">
                          us-west-2: {entry.existing_acm_arn2}
                        </Typography>
                      )}
                    </Box>
                  }
                  sx={{ alignItems: 'flex-start', mb: 0.5, border: '1px solid', borderColor: isSelected ? 'primary.main' : 'divider', borderRadius: 1, px: 1, mx: 0 }}
                />
              )
            })}
          </RadioGroup>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCaSelectDialog({ open: false, rowId: null, commonName: '', entries: [], selectedIdx: 0 })}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => {
              const { rowId, commonName, entries, selectedIdx } = caSelectDialog
              setCaSelectDialog({ open: false, rowId: null, commonName: '', entries: [], selectedIdx: 0 })
              applyAcmEntry(rowId, commonName, entries[selectedIdx])
            }}
          >
            Use This Certificate
          </Button>
        </DialogActions>
      </Dialog>

      {/* ARN Selection Dialog — shown when ALL ACM ARNs for a CN have InUse=false
          and the cert exists across more than one AWS account.
          The AWS account must be onboarded before proceeding. */}
      {(() => {
        const { open, rowId, commonName, entries, selectedIdx } = arnSelectDialog
        const selectedEntry = entries[selectedIdx] || null

        // Only gate on account onboarding — a cert may exist in one or both regions,
        // so having only arn1 or only arn2 is perfectly valid.
        let accountOnboarded = false
        let notOnboardedAccountLabel = ''
        if (selectedEntry && selectedEntry.aws_account_id) {
          const accountMatches = flattenedAccounts.filter(
            a => a.account_number === selectedEntry.aws_account_id
          )
          if (accountMatches.length > 0) {
            accountOnboarded = true
          } else {
            notOnboardedAccountLabel = selectedEntry.aws_account_name
              ? `"${selectedEntry.aws_account_name}" (${selectedEntry.aws_account_id})`
              : `account ${selectedEntry.aws_account_id}`
          }
        }

        return (
          <Dialog
            open={open}
            onClose={() => setArnSelectDialog({ open: false, rowId: null, commonName: '', entries: [], selectedIdx: 0 })}
            maxWidth="sm"
            fullWidth
          >
            <DialogTitle>
              Select ACM ARNs — Certificate Not In Use
              <Typography variant="caption" display="block" color="text.secondary" sx={{ wordBreak: 'break-all' }}>
                {commonName}
              </Typography>
            </DialogTitle>
            <DialogContent dividers>
              <Alert severity="warning" sx={{ mb: 2 }}>
                All ACM ARNs for this certificate have <strong>InUse = false</strong> across all accounts.
                Select which account's ARNs to use. The AWS account must be onboarded to proceed.
                A cert may exist in one or both regions — having only one regional ARN is valid.
              </Alert>
              <RadioGroup
                value={selectedIdx}
                onChange={e => setArnSelectDialog(prev => ({ ...prev, selectedIdx: Number(e.target.value) }))}
              >
                {entries.map((entry, idx) => {
                  const isSelected = selectedIdx === idx
                  const accountLabel = entry.aws_account_name || entry.aws_account_id || 'Unknown Account'
                  const issuerLower = (entry.issuer || '').toLowerCase()
                  const caLabel = issuerLower.includes('digicert') ? 'DigiCert'
                                : issuerLower.includes('broadridge financial solutions') ? 'Entrust'
                                : entry.issuer || 'Unknown CA'
                  const entryOnboarded = flattenedAccounts.some(a => a.account_number === entry.aws_account_id)
                  return (
                    <FormControlLabel
                      key={idx}
                      value={idx}
                      disabled={!entryOnboarded}
                      control={<Radio size="small" />}
                      label={
                        <Box sx={{ py: 0.5 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {accountLabel}
                            {!entryOnboarded && (
                              <Chip
                                label={`Not onboarded: ${entry.aws_account_name || entry.aws_account_id}`}
                                size="small"
                                color="error"
                                sx={{ ml: 1 }}
                              />
                            )}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" display="block">
                            CA: {caLabel}
                            {entry.status && (
                              <Chip
                                label={entry.status}
                                size="small"
                                color={entry.status === 'ISSUED' ? 'success' : entry.status === 'EXPIRED' ? 'error' : 'default'}
                                sx={{ ml: 1, height: 16, fontSize: '0.65rem' }}
                              />
                            )}
                          </Typography>
                          {entry.existing_acm_arn1 && (
                            <Typography variant="caption" color="text.secondary" display="block">
                              us-east-1: {entry.existing_acm_arn1}
                            </Typography>
                          )}
                          {entry.existing_acm_arn2 && (
                            <Typography variant="caption" color="text.secondary" display="block">
                              us-west-2: {entry.existing_acm_arn2}
                            </Typography>
                          )}
                        </Box>
                      }
                      sx={{ alignItems: 'flex-start', mb: 0.5, border: '1px solid', borderColor: isSelected ? 'primary.main' : 'divider', borderRadius: 1, px: 1, mx: 0, opacity: !entryOnboarded ? 0.5 : 1 }}
                    />
                  )
                })}
              </RadioGroup>
              {selectedEntry && !accountOnboarded && notOnboardedAccountLabel && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  Cannot proceed: {notOnboardedAccountLabel} is not onboarded in CertManage.
                  Please onboard this AWS account before onboarding certificates from it.
                </Alert>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setArnSelectDialog({ open: false, rowId: null, commonName: '', entries: [], selectedIdx: 0 })}>
                Cancel
              </Button>
              <Button
                variant="contained"
                disabled={!accountOnboarded}
                onClick={() => {
                  setArnSelectDialog({ open: false, rowId: null, commonName: '', entries: [], selectedIdx: 0 })
                  applyAcmEntry(rowId, commonName, entries[selectedIdx])
                }}
              >
                Use These ARNs
              </Button>
            </DialogActions>
          </Dialog>
        )
      })()}

      {/* Duplicate Common Name Resolution Dialog */}
      <Dialog
        open={duplicateDialog.open}
        onClose={() => setDuplicateDialog({ open: false, groups: [], source: '', allRows: [] })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Duplicate Common Names Found</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            The following common names appear more than once. Select which entry to keep for each — the others will be removed.
          </Typography>
          {duplicateDialog.groups.map((group, gi) => (
            <Box key={group.commonName} sx={{ mb: gi < duplicateDialog.groups.length - 1 ? 3 : 0 }}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700, wordBreak: 'break-all' }}>
                {group.commonName}
              </Typography>
              <RadioGroup
                value={group.selectedId}
                onChange={e => {
                  const newId = Number(e.target.value)
                  setDuplicateDialog(prev => ({
                    ...prev,
                    groups: prev.groups.map((g, i) => i === gi ? { ...g, selectedId: newId } : g),
                  }))
                }}
              >
                {group.rows.map((row, ri) => (
                  <FormControlLabel
                    key={row._id}
                    value={row._id}
                    control={<Radio size="small" />}
                    label={
                      <Box sx={{ py: 0.5 }}>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          Row {duplicateDialog.allRows.findIndex(r => r._id === row._id) + 1}
                          {row.account_name ? ` — ${row.account_name}${row.environment ? ` (${row.environment.toUpperCase()})` : ''}` : ''}
                        </Typography>
                        {row.itsm_group && (
                          <Typography variant="caption" color="text.secondary">
                            ITSM: {row.itsm_group}
                          </Typography>
                        )}
                        {row.venafi_policy_base && (
                          <Typography variant="caption" color="text.secondary" sx={{ ml: row.itsm_group ? 1.5 : 0 }}>
                            Policy: {row.venafi_policy_base}
                          </Typography>
                        )}
                      </Box>
                    }
                    sx={{ alignItems: 'flex-start', mb: 0.5, border: '1px solid', borderColor: group.selectedId === row._id ? 'primary.main' : 'divider', borderRadius: 1, px: 1, mx: 0 }}
                  />
                ))}
              </RadioGroup>
            </Box>
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDuplicateDialog({ open: false, groups: [], source: '', allRows: [] })}>Cancel</Button>
          <Button variant="contained" onClick={handleDuplicateResolve}>Resolve &amp; Continue</Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default BulkCertificateUpload
